/**
 * Widget de devis en ligne — M. Auto Sud Loire
 * Vanilla JS, zéro dépendance. Se monte sur chaque <div data-mauto-devis-widget>.
 *
 * Parcours en 4 étapes façon ad.fr :
 *   1. Prestation  — barre de recherche/dropdown/icônes ; "Autre" révèle un champ libre
 *      obligatoire immédiatement à cette étape.
 *   2. Véhicule    — immatriculation (format FR validé) + marque + modèle, avec un chip
 *      "VOUS AVEZ SÉLECTIONNÉ : ... ✕" pour changer de prestation sans perdre ces champs.
 *   3. Estimation  — prix selon le mode (fourchette/fixe/à_partir_de) ou message sur_devis,
 *      + premier créneau envisageable (≥72h). Prix connu : deux choix distincts, "Prendre
 *      rendez-vous" (passe la main au widget RDV dédié, cf. handoffToRdv) ou "Demander un
 *      devis" (continue vers l'étape 4 ci-dessous). Sur_devis : uniquement la popup dédiée,
 *      pas de choix (jamais schedulable sans diagnostic préalable).
 *   4. Coordonnées — nom/téléphone/email, bouton final "Demander un devis" (envoie toujours
 *      vers /api/devis — ce flux ne réserve jamais un créneau directement, cf. étape 3).
 * Naviguer en arrière ne perd jamais les champs déjà saisis (conservés dans `state`).
 *
 * Configuration (dans l'ordre de priorité) :
 *   1. attribut `data-api-base` sur le conteneur
 *   2. `window.MAUTO_DEVIS_API_BASE`
 *
 * Pré-remplissage prestation (posé par la section Liquid) :
 *   data-prestation-key="vidange"        → clé exacte d'une prestation → étape 1 sautée
 *   data-prestation-key="freinage"       → nom de "famille" → première variante pré-sélectionnée,
 *                                           étape 1 sautée aussi (dropdown restreint accessible
 *                                           via "← Retour", cf. FAMILIES ci-dessous)
 *   data-prestation-key="climatisation"  → famille "à choix forcé" (cf. FORCE_CHOICE) : affiche
 *                                           les forfaits côte à côte, ne pré-sélectionne rien
 *   (absent)                             → étape 1 complète (dropdown ou barre de recherche)
 *
 * Bloc hero (page d'accueil) :
 *   data-variant="hero"                  → classe CSS mauto-devis--hero
 *   data-quick-picks="vidange,freinage,distribution,climatisation"
 *                                         → rangée d'icônes de raccourci ; cliquer une icône
 *                                           équivaut à chercher/choisir cette prestation
 */
(function () {
  'use strict';

  // Familles : une page prestation peut couvrir plusieurs clés tarifaires.
  // Note : "decalaminage" (VP/utilitaire fusionnés en une seule clé tarifaire depuis la grille
  // 2026) n'est plus une famille — une page data-prestation-key="decalaminage" utilise
  // désormais le chemin isSingle (clé directe), plus isFamily.
  var FAMILIES = {
    freinage: ['plaquettes_av', 'plaquettes_ar', 'disques_plaquettes_av', 'disques_plaquettes_ar'],
    amortisseurs: ['amortisseurs_av', 'amortisseurs_ar'],
    climatisation: ['climatisation_recharge', 'climatisation_entretien'],
  };
  // Libellés d'affichage pour les entrées qui référencent une famille (ex. "freinage")
  // plutôt qu'une clé tarifaire directe (ex. "vidange").
  var FAMILY_LABELS = {
    freinage: 'Freinage',
    amortisseurs: 'Suspension',
    climatisation: 'Climatisation',
  };
  // Familles où l'utilisateur doit explicitement choisir entre les variantes (forfaits
  // distincts, pas de "prestation par défaut" raisonnable) — affichées en cartes côte à côte,
  // jamais pré-sélectionnées automatiquement contrairement aux autres familles.
  var FORCE_CHOICE = { climatisation: true };

  // Doit rester alignée avec la règle serveur (sync-server/server.js, DEVIS_MIN_DELAY_H / nextEligibleSlot).
  // Affichage uniquement — le serveur recalcule et fait foi à la soumission.
  var DEVIS_MIN_DELAY_H = 72;

  function nextEligibleSlot() {
    var d = new Date(Date.now() + DEVIS_MIN_DELAY_H * 3600 * 1000);
    var day = d.getDay(); // 0 = dimanche, 6 = samedi
    if (day === 6) d.setDate(d.getDate() + 2);
    else if (day === 0) d.setDate(d.getDate() + 1);
    return d;
  }

  function formatDateLong(d) {
    try {
      return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    } catch (e) {
      return d.toISOString().slice(0, 10);
    }
  }

  // Affiche "64,90€" pour un prix avec décimales, "88€" pour un prix rond (pas de ",00" superflu).
  function formatEuro(n) {
    return Number.isInteger(n) ? (n + '€') : (n.toFixed(2).replace('.', ',') + '€');
  }

  // Message unique pour toute prestation en mode sur_devis (y compris "Autre") — cahier des
  // charges définitif : un seul message, pas de variantes selon la raison.
  var SUR_DEVIS_MESSAGE = "Cette intervention nécessite un diagnostic pour vous proposer un prix précis. Laissez-nous votre email, nous vous recontactons sous 24h ouvrées avec un devis chiffré.";

  function formatPrixAffiche(p) {
    if (!p) return '';
    var base;
    if (p.mode === 'fixe') base = formatEuro(p.prix_fixe) + ' TTC';
    else if (p.mode === 'sur_devis') return SUR_DEVIS_MESSAGE;
    else if (p.min != null && p.max != null) base = 'Entre ' + formatEuro(p.min) + ' et ' + formatEuro(p.max) + ' TTC';
    else if (p.a_partir_de != null) base = 'À partir de ' + formatEuro(p.a_partir_de) + ' TTC';
    else return '';
    if (p.supplement_recent != null) {
      base += ' (+' + formatEuro(p.supplement_recent) + ' pour véhicules 2013 ou plus récents)';
    }
    return base;
  }

  // Prestation "Autre" : pas une clé tarifaire réelle (jamais renvoyée par /api/pricing),
  // injectée côté client à la fin de la liste sélectionnable. Toujours sur_devis.
  var AUTRE_KEY = 'autre';
  var AUTRE_PRESTATION = { label: 'Autre (précisez)', mode: 'sur_devis' };

  // Pare-brise : pas davantage une clé tarifaire réelle (le vitrage se chiffre au cas par cas),
  // mais il a son propre parcours de questions (vitrage, caméra ADAS, assurance, véhicule de
  // prêt) au lieu du champ libre « Autre ». Les réponses sont recopiées dans la description
  // envoyée au garage : aucun champ supplémentaire n'est attendu côté sync-server.
  var PB_KEY = 'pare_brise';
  var PB_PRESTATION = { label: 'Pare-brise (remplacement)', mode: 'sur_devis' };

  // Liste "générale" (barre de recherche + dropdown complet de /pages/devis-en-ligne) — toutes
  // les prestations de la grille tarifaire, dans l'ordre de DEFAULT_PRICING (sync-server/server.js).
  // N'affecte pas les pages prestation dédiées (familles ou clé unique), qui gardent leur propre
  // périmètre inchangé.
  var GENERAL_LIST_KEYS = [
    'vidange', 'revision', 'controle_technique', 'plaquettes_av', 'plaquettes_ar',
    'disques_plaquettes_av', 'disques_plaquettes_ar', 'distribution', 'embrayage',
    'amortisseurs_av', 'amortisseurs_ar', 'geometrie', 'climatisation_recharge',
    'climatisation_entretien', 'batterie', 'pneus', 'diagnostic', 'decalaminage',
  ];

  // Dimension pneu obligatoire — déclenché uniquement pour la clé tarifaire "pneus" (doit
  // rester aligné avec DEVIS_PNEU_KEY côté sync-server/server.js). Mêmes listes et même
  // logique que le widget "Prendre RDV" (devis-rdv-widget.js) — dupliqué ici car les deux
  // widgets sont des fichiers/IIFE totalement indépendants, sans état partagé.
  var DEVIS_PNEU_KEY = 'pneus';
  var NE_SAIT_PAS = 'ne_sait_pas';
  // Les listes largeur/hauteur/diamètre/charge/vitesse ne sont plus écrites ici. Elles étaient
  // figées en dur (PNEU_CHARGES = ['89','93'], PNEU_VITESSES = ['H','V','W','Y','Z']) et rendaient
  // INVISIBLES 82 des 89 fiches publiées, dont les trois 175/65 R14 en 86T et 82H : le client ne
  // pouvait pas saisir son besoin, et la page résultats — qui écarte à juste titre tout pneu
  // d'indice inférieur au besoin — ne renvoyait rien. Elles viennent désormais du catalogue lui-même,
  // via assets/mauto-pneu-dimensions.js (défaut relevé et corrigé le 11/09/2026).
  // Seules restent ici les saisons : ce n'est pas une caractéristique dimensionnelle, la liste des
  // quatre choix possibles est fermée et le libellé « Toutes » n'existe dans aucune fiche.
  var PNEU_SAISONS = [
    { key: 'ete', label: 'Été' },
    { key: '4saisons', label: '4 saisons' },
    { key: 'hiver', label: 'Hiver' },
    { key: 'toutes', label: 'Toutes' },
  ];
  function pneuSaisonLabel(key) {
    var found = PNEU_SAISONS.filter(function (s) { return s.key === key; })[0];
    return found ? found.label : '';
  }
  function pneuSaisonKey(label) {
    var found = PNEU_SAISONS.filter(function (s) { return s.label === label; })[0];
    return found ? found.key : '';
  }

  // Index des dimensions réellement publiées, chargé une fois depuis /collections/pneus?view=
  // pneus-index (cf. assets/mauto-pneu-dimensions.js et templates/collection.pneus-index.liquid).
  // `pneuIndex` reste null tant que la promesse n'a pas abouti ; pneuOptions() sait fonctionner
  // dans cet état, de même que si le fichier partagé n'a pas pu se charger du tout — dans les deux
  // cas on retombe sur les échelles normalisées, jamais sur une liste vide qui bloquerait le client.
  var pneuIndex = null;
  function chargerPneuIndex() {
    var mod = window.MautoPneuDimensions;
    if (!mod) return Promise.resolve(null);
    if (pneuIndex) return Promise.resolve(pneuIndex);
    return mod.charger().then(function (idx) { pneuIndex = idx; return idx; });
  }
  function pneuOptions(champ, sel) {
    var mod = window.MautoPneuDimensions;
    if (!mod) return { dispo: [], toutes: [] };
    return mod.options(pneuIndex, champ, sel);
  }

  // Saisons proposées dans le tiroir : celles réellement publiées au catalogue, plus « Toutes »
  // qui n'est pas une saison mais l'absence de préférence. Le catalogue ne contient que de l'Été
  // au 11/09/2026 : proposer « Hiver » ou « 4 saisons » envoyait le client vers une page de
  // résultats vide, ce qui donne l'impression d'un site cassé. Elles réapparaîtront d'elles-mêmes
  // dès qu'un pneu hiver sera publié, sans retoucher ce fichier.
  // Si l'index n'a pas pu être chargé, on affiche tout : mieux vaut un choix trop large qu'un
  // parcours amputé par un incident réseau.
  var SAISON_EXPLICATIONS = {
    ete: 'Été&nbsp;: meilleure tenue de route par temps sec/chaud.',
    hiver: 'Hiver&nbsp;: adhérence en dessous de 7°C, neige/verglas.',
    '4saisons': '4 saisons&nbsp;: compromis toute l\'année sans changer de jeu.',
  };
  function saisonsProposees() {
    var dispo = {};
    if (pneuIndex && pneuIndex.fiches) {
      pneuIndex.fiches.forEach(function (f) { if (f.saison) dispo[f.saison] = 1; });
    }
    var indexVide = !Object.keys(dispo).length;
    return PNEU_SAISONS.filter(function (s) {
      return indexVide || s.key === 'toutes' || dispo[s.label];
    });
  }

  // Mode "commande" : la page résultats renvoie ici quand elle n'a AUCUN pneu de la dimension
  // demandée (cf. lienCommande dans assets/pneus-resultats.js). Le client a déjà tout saisi dans
  // le tiroir — lui refaire les 6 étapes serait la boucle qu'on cherche justement à éviter. On
  // repart donc de ses réponses, et on ne le renvoie pas vers les résultats qu'il vient de
  // quitter les mains vides. Les noms de paramètres doivent rester alignés avec lienCommande.
  function readCommandeParams() {
    var p = new URLSearchParams(window.location.search);
    if (p.get('commande') !== '1') return null;
    var largeur = p.get('largeur') || '';
    var hauteur = p.get('hauteur') || '';
    var diametre = p.get('diametre') || '';
    if (!largeur || !hauteur || !diametre) return null;   // dimension incomplète : mode ignoré
    return {
      largeur: largeur,
      hauteur: hauteur,
      diametre: diametre,
      indiceCharge: p.get('charge') || '',
      indiceVitesse: p.get('vitesse') || '',
      saison: pneuSaisonKey(p.get('saison') || ''),
      vehiculeElectrique: false,
      quantite: p.get('quantite') || '',
    };
  }

  // Partie 3 — une fois le tiroir dimension complété, le parcours ne continue plus dans ce
  // widget (pneus n'a jamais eu de vrai prix chiffrable en ligne, cf. l'ancien popup sur_devis) :
  // il redirige vers la page résultats (collection "Pneus" filtrée par dimension), cf.
  // endPneuFlow. L'URL utilise des query params (pas de vrai slug, contrainte Shopify) — le nom
  // des paramètres doit rester aligné avec assets/pneus-resultats.js.
  var PNEU_RESULTATS_URL = '/collections/pneus';
  function buildPneuResultsUrl(pneu) {
    var params = new URLSearchParams();
    if (pneu.largeur) params.set('largeur', pneu.largeur);
    if (pneu.hauteur) params.set('hauteur', pneu.hauteur);
    if (pneu.diametre) params.set('diametre', pneu.diametre);
    // Les indices voyagent jusqu'aux résultats : ils y servent à écarter les pneus dont un indice
    // est INFÉRIEUR au besoin (interdit au montage), cf. assets/pneus-resultats.js.
    // « Je ne sais pas » ne doit surtout pas être transmis : côté résultats, parseInt('ne_sait_pas')
    // vaut NaN, toutes les comparaisons deviennent fausses et chaque pneu se retrouve étiqueté
    // « indices supérieurs — compatible » alors qu'aucun besoin n'a été exprimé. Un indice inconnu
    // s'envoie en ne l'envoyant pas : la dimension seule filtre alors les résultats.
    if (pneu.indiceCharge && pneu.indiceCharge !== NE_SAIT_PAS) params.set('charge', pneu.indiceCharge);
    if (pneu.indiceVitesse && pneu.indiceVitesse !== NE_SAIT_PAS) params.set('vitesse', pneu.indiceVitesse);
    var saisonLabel = pneuSaisonLabel(pneu.saison);
    if (saisonLabel) params.set('saison', saisonLabel);
    if (pneu.quantite) params.set('quantite', pneu.quantite);
    return PNEU_RESULTATS_URL + '?' + params.toString();
  }

  // Ancienne passerelle "Ajouter" de la page résultats pneus vers ce widget. Plus personne
  // n'écrit cette clé : depuis le passage des pneus au panier Shopify (paiement intégral en
  // ligne, cf. assets/pneus-resultats.js), le bouton "Ajouter" ne passe plus par le devis.
  // La lecture est conservée pour vider proprement un reliquat laissé dans le navigateur d'un
  // client avant ce changement — à supprimer d'ici quelques mois.
  var PNEU_CART_HANDOFF_KEY = 'mautoPendingPneuCartItem';
  function consumePendingPneuCartItem() {
    var raw;
    try { raw = window.localStorage.getItem(PNEU_CART_HANDOFF_KEY); } catch (e) { return null; }
    if (!raw) return null;
    try { window.localStorage.removeItem(PNEU_CART_HANDOFF_KEY); } catch (e) { /* ignoré */ }
    try {
      var item = JSON.parse(raw);
      return (item && item.type === 'pneu_produit') ? item : null;
    } catch (e) {
      return null;
    }
  }

  // Montant chiffré d'un item pneu_produit (toujours chiffré, jamais sur_devis) : (prix pneu +
  // prix montage) unitaires × quantité. Mêmes unités que la page résultats (déjà en euros, pas
  // en centimes — conversion faite une fois pour toutes côté assets/pneus-resultats.js).
  function pneuProduitMontant(item) {
    var qte = parseInt(item.quantite, 10) || 0;
    var unitaire = (Number(item.prixPneuUnitaire) || 0) + (Number(item.prixMontageUnitaire) || 0);
    return unitaire * qte;
  }

  // Immatriculation française format SIV (ex. AB-123-CD) — tirets ou espaces optionnels.
  var IMMAT_REGEX = /^[A-Za-z]{2}[\s-]?\d{3}[\s-]?[A-Za-z]{2}$/;
  function normalizeImmat(v) {
    var compact = v.replace(/[\s-]/g, '').toUpperCase();
    if (compact.length === 7) return compact.slice(0, 2) + '-' + compact.slice(2, 5) + '-' + compact.slice(5, 7);
    return v.trim().toUpperCase();
  }

  // Icônes du parcours : toutes dans assets/mauto-icones.js, partagé avec le widget de
  // rendez-vous. Elles vivaient ici, donc le widget de rendez-vous n'en avait aucune et le
  // client ne voyait pas la même chose selon le bouton cliqué. Une seule source désormais.
  // Si le fichier partagé n'a pas pu se charger, on renvoie une chaîne vide : le libellé reste
  // lisible seul, mieux vaut une vignette sans dessin qu'un carré vide.
  function icone(cle) {
    var m = window.MautoIcones;
    return m ? m.pour(cle) : '';
  }
  function iconFor(key) {
    var m = window.MautoIcones;
    if (!m) return '';
    // « Entretien » et « Vidange » partagent la burette : c'est la même famille pour le client.
    return m.pour(key === 'revision' ? 'vidange' : key, 'autre');
  }

  // Libellés courts réservés aux vignettes de raccourci. Les libellés tarifaires officiels sont
  // beaucoup trop longs pour une vignette — « Pack Contrôle technique - Contre visite
  // remboursés*** » déborderait — mais ils restent inchangés partout ailleurs : seul l'affichage
  // des vignettes est raccourci ici.
  var QUICK_LABELS = {
    pneus: 'Pneus', vidange: 'Vidange', revision: 'Entretien', controle_technique: 'Pack CT',
    freinage: 'Freinage', distribution: 'Distribution', amortisseurs: 'Suspension',
    geometrie: 'Géométrie', climatisation: 'Climatisation', decalaminage: 'Décalaminage',
    diagnostic: 'Diagnostic', pare_brise: 'Pare-brise', autre: 'Autre',
  };

  // Vignettes qui ne correspondent à AUCUNE clé tarifaire et passent donc par « Autre
  // (précisez) », avec la description déjà remplie. Le pare-brise est dans ce cas : il n'existe
  // pas de tarif « pare_brise » dans l'application (29 clés au 11/09/2026, aucune pour le
  // vitrage). Sans ce détour, la vignette serait silencieusement ignorée au rendu — la boucle
  // d'affichage écarte toute clé absente de state.pricing.
  // Le jour où une vraie clé tarifaire « pare_brise » est créée dans l'application, il suffit de
  // retirer l'entrée ci-dessous : la vignette bascule alors toute seule sur le parcours normal,
  // avec estimation de prix au lieu d'une demande de devis.
  var QUICK_AUTRE = {
    pare_brise: 'Pare-brise (remplacement)',
  };

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function normalize(s) {
    return String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  }

  function el(html) {
    var t = document.createElement('template');
    t.innerHTML = html.trim();
    return t.content.firstElementChild;
  }

  function getApiBase(container) {
    return container.getAttribute('data-api-base') || window.MAUTO_DEVIS_API_BASE || '';
  }

  function initWidget(container) {
    var apiBase = getApiBase(container);
    if (!apiBase) {
      container.innerHTML = '<p class="mauto-devis-error">Widget de devis non configuré (API manquante).</p>';
      return;
    }

    var presetKey = container.getAttribute('data-prestation-key') || '';
    var isFamily = !!FAMILIES[presetKey];
    var isSingle = !isFamily && !!presetKey;

    var quickPicks = (container.getAttribute('data-quick-picks') || '')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    var variant = container.getAttribute('data-variant') || '';
    var useSearchBar = variant === 'hero';

    // État persistant du parcours — jamais réinitialisé en cours de route, pour que "← Retour"
    // ne fasse jamais perdre ce qui a déjà été saisi.
    var state = {
      pricing: null,
      prestationKey: '',
      autreDescription: '',
      vehicule: { immatriculation: '', marque: '', modele: '' },
      pb: { vitrage: '', dommage: '', adas: '', assureur: '', brisGlace: '', prenom: '', nom: '', date: '', creneau: '', note: '', pret: '', photos: [] },
      coordonnees: { nom: '', telephone: '', email: '' },
      pneu: { largeur: '', hauteur: '', diametre: '', indiceCharge: '', indiceVitesse: '', saison: '', vehiculeElectrique: false, quantite: '' },
      // Vrai uniquement pour la prestation en cours, quand elle vient de l'état vide des
      // résultats (dimension hors stock) : empêche endPneuFlow d'y renvoyer le client.
      pneuCommande: false,
      // Panier (Partie 2, ad.fr-style) : prestations déjà "ajoutées" via le bouton dédié à
      // l'étape Estimation, en plus de celle en cours (prestationKey/pneu/autreDescription
      // ci-dessus). Vide = comportement mono-prestation strictement inchangé (même payload,
      // même endpoint /api/devis). Le véhicule et les coordonnées restent partagés par tout le
      // panier — jamais redemandés d'un item à l'autre.
      cart: [],
      step: 1,
    };
    // Récupère un éventuel item pneu ajouté depuis la page résultats (Partie 3) avant ce montage
    // — une seule fois : consumePendingPneuCartItem() vide la clé localStorage dès la lecture,
    // donc un second widget monté sur la même page (ex. le widget flottant après le widget de
    // page) ne le récupère pas une deuxième fois.
    var pendingPneuItem = consumePendingPneuCartItem();
    if (pendingPneuItem) state.cart.push(pendingPneuItem);
    // Où renvoie "← Retour" depuis l'étape Véhicule — dépend du contexte (recherche libre,
    // dropdown scopé à une famille, cartes de forfaits...). null = pas de bouton retour
    // (page prestation à clé unique, rien à choisir d'autre).
    var goBackFromVehicule = null;
    // Sur mobile, le bloc hero affiche d'abord un simple bouton "Choisir votre prestation" à la
    // place de la barre de recherche — une fois cliqué (heroEntered = true), la vraie étape 1
    // (recherche + autocomplétion) s'affiche en plein écran comme les autres étapes, et le reste
    // du parcours (y compris un retour ultérieur à l'étape 1) ne revoit plus jamais ce bouton
    // compact pour cette instance du widget. Desktop inchangé (jamais concerné, cf. usage plus bas).
    var heroEntered = false;

    container.innerHTML = '';
    container.classList.add('mauto-devis');
    if (variant) container.classList.add('mauto-devis--' + variant);

    // Mode plein écran mobile (<768px, cf. devis-widget.css) : la carte flottante devient un
    // overlay fixed couvrant tout le viewport. Sur la variante "hero" (page d'accueil), le bloc
    // hero (image/titre/barre de recherche) reste visuellement inchangé tant qu'aucune
    // prestation n'est choisie — le plein écran n'active qu'à partir de ce moment-là (cf.
    // setFlowActive). Sur les autres pages (dropdown intégré à la page), il n'y a pas de "bloc
    // hero" à préserver : le plein écran est actif en permanence, dès le montage.
    var currentBackHandler = null;
    // variant="inline" : widget posé au milieu d'une page de contenu (page prestation).
    // Comme la variante hero, il reste à sa place tant que le visiteur n'a rien choisi — sinon
    // il couvrirait toute la page dès le chargement sur mobile et masquerait le contenu.
    var keepsPlaceUntilChoice = useSearchBar || variant === 'inline';
    // Sur une page de contenu, une prestation pré-remplie (data-prestation-key) fait démarrer le
    // parcours dès le chargement : sans ce garde-fou, le plein écran mobile s'ouvrirait tout seul
    // devant une page que le visiteur n'a pas encore lue. Il ne bascule qu'au premier clic réel.
    var userEngaged = false;
    container.addEventListener('click', function () { userEngaged = true; }, true);
    function setFlowActive(active) {
      if (!keepsPlaceUntilChoice) return;
      if (variant === 'inline' && active && !userEngaged) {
        container.classList.remove('mauto-devis-flow-active');
        return;
      }
      container.classList.toggle('mauto-devis-flow-active', active);
      document.body.classList.toggle('mauto-devis-scroll-lock', active);
    }
    if (!keepsPlaceUntilChoice) container.classList.add('mauto-devis-flow-active');

    var mobileTopbar = el(
      '<div class="mauto-devis-mobile-topbar">' +
        '<button type="button" class="mauto-devis-mobile-back is-invisible" aria-label="Retour">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>' +
        '</button>' +
        '<div class="mauto-devis-mobile-progress">' +
          '<span class="mauto-devis-mobile-dot" data-n="1"></span>' +
          '<span class="mauto-devis-mobile-dot" data-n="2"></span>' +
          '<span class="mauto-devis-mobile-dot" data-n="3"></span>' +
          '<span class="mauto-devis-mobile-dot" data-n="4"></span>' +
        '</div>' +
      '</div>'
    );
    container.appendChild(mobileTopbar);
    var mobileBackBtn = mobileTopbar.querySelector('.mauto-devis-mobile-back');
    mobileBackBtn.addEventListener('click', function () { if (currentBackHandler) currentBackHandler(); });

    var stepsBar = el(
      '<div class="mauto-devis-stepsbar">' +
        '<span class="mauto-devis-stepdot" data-n="1">1. Prestation</span>' +
        '<span class="mauto-devis-stepdot" data-n="2">2. Véhicule</span>' +
        '<span class="mauto-devis-stepdot" data-n="3">3. Estimation</span>' +
        '<span class="mauto-devis-stepdot" data-n="4">4. Coordonnées</span>' +
      '</div>'
    );
    container.appendChild(stepsBar);

    var quickPicksBar = null;
    if (quickPicks.length) {
      quickPicksBar = el('<div class="mauto-devis-quickpicks"></div>');
      container.appendChild(quickPicksBar);
    }

    // Barre panier persistante (Partie 2) — masquée tant que state.cart est vide, donc invisible
    // sur tout parcours mono-prestation classique. Reste affichée et interactive (retrait d'un
    // item) sur toutes les étapes, y compris Coordonnées, sans dupliquer son rendu ailleurs.
    var cartBar = el('<div class="mauto-devis-cart-bar" hidden></div>');
    container.appendChild(cartBar);

    var body = el('<div class="mauto-devis-body"></div>');
    container.appendChild(body);

    function cartItemLabel(item) {
      if (item.type === 'pneu_produit') {
        return (item.marque || '') + ' ' + (item.modele || '') + ' (pneus ' + (item.dimension || '') + ')';
      }
      var p = item.prestationKey === AUTRE_KEY ? AUTRE_PRESTATION : state.pricing[item.prestationKey];
      return p ? p.label : item.prestationKey;
    }

    // Jusqu'au bout du panier sans passer par une autre prestation : efface toute sélection en
    // cours (jamais "ajoutée" donc jamais comptée) avant de sauter directement aux Coordonnées —
    // utile en particulier pour un panier composé uniquement de pneus (results page), qui n'a
    // jamais besoin de choisir de prestation classique.
    function validateCartOnly() {
      state.prestationKey = '';
      state.autreDescription = '';
      state.pneu = { largeur: '', hauteur: '', diametre: '', indiceCharge: '', indiceVitesse: '', saison: '', vehiculeElectrique: false, quantite: '' };
      state.pneuCommande = false;
      // Un panier composé uniquement de pneus n'est jamais passé par l'étape Véhicule (le tiroir
      // pneu redirige directement vers la page résultats, cf. endPneuFlow) — la demander ici une
      // seule fois si elle manque encore, sinon (déjà connue via une autre prestation) filer
      // directement aux Coordonnées.
      var vehiculeKnown = state.vehicule.immatriculation && state.vehicule.marque && state.vehicule.modele;
      if (vehiculeKnown) {
        goToStep(4);
      } else {
        goBackFromVehicule = renderStep1;
        goToStep(2);
      }
    }

    function renderCartBar() {
      if (!state.cart.length) {
        cartBar.hidden = true;
        cartBar.innerHTML = '';
        return;
      }
      cartBar.hidden = false;
      var itemsHtml = state.cart.map(function (item, idx) {
        return '<span class="mauto-devis-cart-item">' + esc(cartItemLabel(item)) +
          '<button type="button" class="mauto-devis-cart-remove" data-idx="' + idx + '" aria-label="Retirer cette prestation">✕</button></span>';
      }).join('');
      cartBar.innerHTML =
        '<span class="mauto-devis-cart-label">' + icone('panier') + ' ' + state.cart.length + ' prestation' + (state.cart.length > 1 ? 's' : '') + ' ajoutée' + (state.cart.length > 1 ? 's' : '') + '</span>' +
        '<span class="mauto-devis-cart-items">' + itemsHtml + '</span>' +
        (state.step !== 4 ? '<button type="button" class="mauto-devis-cart-validate" data-action="valider-panier">Valider mon panier →</button>' : '');
      var removeBtns = cartBar.querySelectorAll('.mauto-devis-cart-remove');
      for (var i = 0; i < removeBtns.length; i++) {
        removeBtns[i].addEventListener('click', function (ev) {
          state.cart.splice(Number(ev.currentTarget.getAttribute('data-idx')), 1);
          renderCartBar();
        });
      }
      var validateBtn = cartBar.querySelector('[data-action="valider-panier"]');
      if (validateBtn) validateBtn.addEventListener('click', validateCartOnly);
    }

    // `backHandler` : fonction appelée par la flèche de retour de la barre fixe mobile
    // (`.mauto-devis-mobile-back`) — null/absent = pas de retour possible depuis cette étape
    // (flèche rendue invisible mais garde sa place pour ne pas déséquilibrer la barre).
    function setStep(n, backHandler) {
      state.step = n;
      currentBackHandler = backHandler || null;
      mobileBackBtn.classList.toggle('is-invisible', !currentBackHandler);
      var dots = stepsBar.querySelectorAll('.mauto-devis-stepdot');
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', Number(dots[i].getAttribute('data-n')) === n);
        dots[i].classList.toggle('is-done', Number(dots[i].getAttribute('data-n')) < n);
      }
      var mdots = mobileTopbar.querySelectorAll('.mauto-devis-mobile-dot');
      for (var j = 0; j < mdots.length; j++) {
        mdots[j].classList.toggle('is-active', Number(mdots[j].getAttribute('data-n')) === n);
        mdots[j].classList.toggle('is-done', Number(mdots[j].getAttribute('data-n')) < n);
      }
      renderCartBar();
    }

    // Ajoute la prestation en cours au panier et repart sur l'étape 1 pour la suivante. Le
    // véhicule (déjà saisi) et les coordonnées ne sont jamais redemandés — cf. goToVehiculeOrEstimation.
    // Note portée jusqu'à l'e-mail reçu par l'atelier. On passe par `message` plutôt que par un
    // champ ajouté à `pneu` : côté sync-server, sanitizePneu() reconstruit l'objet à partir d'une
    // liste blanche de 8 clés, donc tout champ supplémentaire y serait supprimé en silence.
    //
    // PORTÉE RÉELLE, à connaître : la mention n'arrive à l'atelier que sur le parcours
    // mono-prestation (/api/devis), qui est le chemin nominal d'une commande de pneu — le client
    // vient de l'état vide des résultats et valide une seule prestation. Sur un panier
    // multi-prestations (/api/devis-groupe), resolveDevisItem() côté serveur fait
    // `message: prestationKey === AUTRE_KEY ? messageTrim : ''` et jette donc la note. Corriger
    // ce trou demande une modification du sync-server et un déploiement : à faire à la prochaine
    // occasion de le déployer, pas avant. Aucun déploiement requis pour le cas nominal.
    var MESSAGE_COMMANDE = 'Dimension non disponible en stock — le client demande une commande (prix sous 24 h).';
    function messagePrestation() {
      if (state.prestationKey === AUTRE_KEY) return state.autreDescription;
      if (state.prestationKey === DEVIS_PNEU_KEY && state.pneuCommande) return MESSAGE_COMMANDE;
      return '';
    }

    function addCurrentToCart() {
      state.cart.push({
        prestationKey: state.prestationKey,
        pneu: state.prestationKey === DEVIS_PNEU_KEY ? JSON.parse(JSON.stringify(state.pneu)) : null,
        message: messagePrestation(),
      });
      state.prestationKey = '';
      state.autreDescription = '';
      state.pneu = { largeur: '', hauteur: '', diametre: '', indiceCharge: '', indiceVitesse: '', saison: '', vehiculeElectrique: false, quantite: '' };
      state.pneuCommande = false;
      renderStep1();
    }

    // Point de passage unique après le choix d'une prestation : la toute première passe par
    // l'étape Véhicule comme aujourd'hui ; à partir de la 2e (panier non vide, véhicule déjà
    // connu), l'étape Véhicule est sautée — direction l'Estimation.
    // Le parcours pare-brise emprunte la clé « autre » (la seule dont le sync-server conserve
    // le message libre), mais le client ne doit pas lire « Autre (précisez) » : on réaffiche le
    // vitrage qu'il a choisi.
    function displayLabel(key, p) {
      if (key === AUTRE_KEY && state.pb && state.pb.vitrage) return 'Remplacement ' + state.pb.vitrage.toLowerCase();
      return p.label;
    }

    function goToVehiculeOrEstimation() {
      goToStep(state.cart.length > 0 ? 3 : 2);
    }

    // Symétrique de goToVehiculeOrEstimation pour le "← Retour" depuis l'Estimation : si l'étape
    // Véhicule a été sautée à l'aller (panier non vide), le retour va au choix de prestation (1)
    // plutôt qu'à une étape Véhicule jamais affichée pour cet item.
    function backFromEstimation() {
      goToStep(state.cart.length > 0 ? 1 : 2);
    }

    function goToStep(n) {
      if (n === 1) renderStep1();
      else if (n === 2) renderStepVehicule();
      else if (n === 3) renderStepEstimation();
      else if (n === 4) renderStepCoordonnees();
    }

    function renderLoading() {
      body.innerHTML = '<p class="mauto-devis-loading">Chargement des tarifs…</p>';
    }

    function renderError(msg) {
      body.innerHTML = '<p class="mauto-devis-error">' + esc(msg) + '</p>';
    }

    // Liste "plate" des entrées sélectionnables pour la barre de recherche : une entrée par
    // famille (libellé générique, ex. "Freinage") + une entrée par clé tarifaire qui n'appartient
    // à aucune famille (ex. "vidange").
    function buildSuggestionList() {
      var list = [];
      GENERAL_LIST_KEYS.forEach(function (k) {
        if (state.pricing[k]) list.push({ key: k, label: state.pricing[k].label });
      });
      list.push({ key: PB_KEY, label: PB_PRESTATION.label });
      list.push({ key: AUTRE_KEY, label: AUTRE_PRESTATION.label });
      return list;
    }

    // Résout une entrée choisie (clé directe, nom de famille, ou "Autre") et avance vers
    // l'étape appropriée. Point d'entrée unique utilisé par le dropdown, la barre de
    // recherche et les icônes de raccourci.
    function selectSuggestion(key) {
      // Vignette sans clé tarifaire (cf. QUICK_AUTRE) : on entre dans « Autre (précisez) » avec
      // la description déjà écrite. Le client n'a plus qu'à cliquer sur Continuer, ou compléter.
      // On ne pré-remplit que si le champ est vide : sinon un retour en arrière effacerait ce
      // qu'il vient de taper.
      if (key === PB_KEY) {
        startPareBriseFlow(renderStep1);
        return;
      }
      if (QUICK_AUTRE[key]) {
        if (!String(state.autreDescription || '').trim()) state.autreDescription = QUICK_AUTRE[key];
        renderAutreStep();
        return;
      }
      if (key === AUTRE_KEY) {
        renderAutreStep();
        return;
      }
      if (FAMILIES[key]) {
        if (FORCE_CHOICE[key]) {
          renderFamilyChoice(key, true);
        } else {
          state.prestationKey = FAMILIES[key][0];
          goBackFromVehicule = renderStep1;
          goToVehiculeOrEstimation();
        }
      } else {
        state.prestationKey = key;
        goBackFromVehicule = renderStep1;
        if (key === DEVIS_PNEU_KEY) startPneuFlow(goToVehiculeOrEstimation);
        else goToVehiculeOrEstimation();
      }
    }

    // ─── Tiroir dimension pneu (6 étapes obligatoires, sauf la dernière) ─────────────────────
    // Déclenché uniquement quand la clé tarifaire choisie est DEVIS_PNEU_KEY, avant l'étape
    // Véhicule. `onDone` : callback fourni par l'appelant (selectSuggestion ou le pré-remplissage
    // isSingle) — permet de reprendre le parcours normal (goToStep(2)) quel que soit le point
    // d'entrée. Réutilise les mêmes classes .mauto-pneu-* que devis-rdv-widget.css (partagées,
    // pas dupliquées) et le même principe : mobileTopbar/stepsBar masqués pendant le sous-parcours.
    function pneuSummaryText() {
      var p = state.pneu;
      if (!p.largeur) return '';
      var s = p.largeur;
      if (p.hauteur) s += '/' + p.hauteur;
      if (p.diametre) s += ' R' + p.diametre;
      if (p.indiceCharge) s += ' ' + (p.indiceCharge === NE_SAIT_PAS ? '?' : p.indiceCharge);
      if (p.indiceVitesse) s += (p.indiceVitesse === NE_SAIT_PAS ? '?' : p.indiceVitesse);
      return s;
    }

    // `depuisRecap` : entrée en mode commande, dimension déjà connue. On ouvre directement la
    // dernière étape plutôt que la première — le bandeau de récapitulatif du tiroir montre alors
    // au client que sa dimension a bien été reprise, et il lui reste un clic pour continuer.
    function startPneuFlow(onDone, depuisRecap) {
      setFlowActive(true);
      mobileTopbar.style.display = 'none';
      stepsBar.style.display = 'none';
      if (quickPicksBar) quickPicksBar.style.display = 'none';
      // En mode commande on entre directement à la dernière étape, mais le client peut remonter
      // via « modifier » : on lance le chargement sans attendre, il sera là bien avant lui.
      if (depuisRecap) { chargerPneuIndex(); renderPneuStepSaison(onDone); return; }
      // Les listes sont construites sur le catalogue : tant qu'il n'est pas chargé il n'y a rien de
      // juste à afficher. En pratique l'index fait ~3 Ko et le cache de session évite tout nouvel
      // aller-retour ; l'écran d'attente n'est visible qu'à la toute première ouverture.
      if (pneuIndex) { renderPneuStepLargeur(onDone); return; }
      renderPneuAttente(onDone);
      chargerPneuIndex().then(function () { renderPneuStepLargeur(onDone); });
    }

    // Partie 3 : le tiroir ne débouche plus sur l'étape Véhicule/Estimation de ce widget (pneus
    // n'a jamais eu de prix chiffrable ici, seulement l'ancien popup sur_devis) — il redirige
    // vers la page résultats pneus. `onDone` (toujours fourni par les appelants, cf. startPneuFlow)
    // n'est donc plus appelé ; conservé dans la signature uniquement pour ne pas devoir modifier
    // tous les points d'appel qui le passent encore.
    function endPneuFlow(onDone) {
      // En mode commande, la page résultats a déjà répondu « rien en stock » pour cette
      // dimension : y renvoyer le client le ramènerait sur l'état vide dont il vient. On
      // poursuit donc le devis normalement.
      if (state.pneuCommande) { goToVehiculeOrEstimation(); return; }
      window.location.href = buildPneuResultsUrl(state.pneu);
    }

    function cancelPneuFlow() {
      mobileTopbar.style.display = '';
      stepsBar.style.display = '';
      if (quickPicksBar) quickPicksBar.style.display = '';
      state.prestationKey = '';
      state.pneu = { largeur: '', hauteur: '', diametre: '', indiceCharge: '', indiceVitesse: '', saison: '', vehiculeElectrique: false, quantite: '' };
      state.pneuCommande = false;
      renderStep1();
    }

    function renderPneuChrome(n, title, backHandler) {
      var pct = Math.round((n / 6) * 100);
      var summary = pneuSummaryText();
      return (
        '<div class="mauto-pneu-header">' +
          (backHandler ? '<button type="button" class="mauto-pneu-back" data-action="pneu-back" aria-label="Retour"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg></button>' : '<span class="mauto-pneu-back-spacer"></span>') +
          '<span class="mauto-pneu-title">Trouver mes pneus</span>' +
          '<button type="button" class="mauto-pneu-close" data-action="pneu-close" aria-label="Fermer">✕</button>' +
        '</div>' +
        (summary ? '<div class="mauto-pneu-recap"><span>Dimension : ' + esc(summary) + '</span><button type="button" class="mauto-pneu-edit" data-action="pneu-edit" aria-label="Modifier la dimension">✎</button></div>' : '') +
        '<div class="mauto-pneu-progress-row"><span>' + esc(title) + '</span><span>' + n + '/6</span></div>' +
        '<div class="mauto-pneu-progressbar"><div style="width:' + pct + '%"></div></div>'
      );
    }

    function wirePneuChrome(backHandler, onDone) {
      // Bug iOS Safari (position:fixed + -webkit-overflow-scrolling:touch, cf. devis-widget.css) :
      // après un remplacement de body.innerHTML, la barre de progression du tiroir pneu peut
      // rester peinte "détachée" en bas de l'écran physique au lieu de sa vraie position dans le
      // flux — le translateZ(0) posé sur .mauto-devis-body ne suffit pas seul dans tous les cas
      // (captures utilisateur iPhone, 2026-08-23). Remettre le scroll en haut et forcer un reflow
      // synchrone à chaque étape corrige le repaint.
      if (window.innerWidth <= 768) {
        body.scrollTop = 0;
        void body.offsetHeight;
      }
      var backBtn = body.querySelector('[data-action="pneu-back"]');
      if (backBtn && backHandler) backBtn.addEventListener('click', backHandler);
      body.querySelector('[data-action="pneu-close"]').addEventListener('click', cancelPneuFlow);
      var editBtn = body.querySelector('[data-action="pneu-edit"]');
      if (editBtn) editBtn.addEventListener('click', function () { renderPneuStepLargeur(onDone); });
      var infoBtn = body.querySelector('[data-action="pneu-info"]');
      if (infoBtn) {
        infoBtn.addEventListener('click', function () {
          var t = body.querySelector('.mauto-pneu-info-text');
          t.hidden = !t.hidden;
        });
      }
    }

    function wirePneuOptions(field, onPick) {
      var btns = body.querySelectorAll('.mauto-pneu-option');
      for (var i = 0; i < btns.length; i++) {
        btns[i].addEventListener('click', function (ev) {
          state.pneu[field] = ev.currentTarget.getAttribute('data-value');
          onPick();
        });
      }
    }

    // ── Grille de choix construite à partir du catalogue ────────────────────────────────────
    // Affiche par défaut les seules valeurs réellement publiées, compte tenu de ce que le client
    // a déjà choisi (les hauteurs du 175, les diamètres du 175/65, les indices du 175/65 R14).
    // L'échelle normalisée complète reste accessible derrière un lien, et c'est indispensable :
    // l'indice saisi est le BESOIN du véhicule, pas une préférence. Un client qui a besoin de 91V
    // devant un catalogue proposant 88H et 94W doit pouvoir saisir 91V — sinon il choisirait
    // « la valeur la plus proche », 88H, et repartirait avec un pneu sous-dimensionné. En saisissant
    // son vrai besoin il obtient le 94W (indices supérieurs, montable) ou un devis sous 24 h.
    // Quand la dimension n'existe pas du tout au catalogue, l'échelle s'affiche directement : il
    // n'y a rien à proposer, mais le client doit pouvoir décrire ce qu'il lui faut.
    function renderPneuGrille(champ, field, labelDispo, labelToutes, lienLabel) {
      var sel = {
        largeur: champ === 'largeur' ? '' : state.pneu.largeur,
        hauteur: (champ === 'largeur' || champ === 'hauteur') ? '' : state.pneu.hauteur,
        diametre: champ === 'diametre' ? '' : state.pneu.diametre,
      };
      var o = pneuOptions(champ, sel);
      var tout = !!state['_pneuTout_' + champ] || o.dispo.length === 0;
      var liste = tout ? o.toutes : o.dispo;
      var auCatalogue = {};
      o.dispo.forEach(function (v) { auCatalogue[v] = 1; });
      return '<p class="mauto-devis-field-label">' + esc(tout ? labelToutes : labelDispo) +
          (tout && o.dispo.length ? ' <span class="mauto-pneu-legende">● disponible chez nous</span>' : '') +
        '</p>' +
        '<div class="mauto-pneu-grid">' +
          liste.map(function (v) {
            var cls = 'mauto-pneu-option' +
              (state.pneu[field] === v ? ' is-selected' : '') +
              (tout && auCatalogue[v] ? ' is-dispo' : '');
            return '<button type="button" class="' + cls + '" data-value="' + esc(v) + '">' + esc(v) + '</button>';
          }).join('') +
        '</div>' +
        (tout ? '' : '<button type="button" class="mauto-pneu-link" data-action="pneu-tout">' + esc(lienLabel) + '</button>');
    }

    function wirePneuGrille(champ, field, nextFn, rerender) {
      var lien = body.querySelector('[data-action="pneu-tout"]');
      if (lien) {
        lien.addEventListener('click', function () {
          state['_pneuTout_' + champ] = true;
          rerender();
        });
      }
      wirePneuOptions(field, nextFn);
    }

    // L'index du catalogue est chargé une seule fois, à l'ouverture du tiroir. Tant qu'il n'est pas
    // là on affiche un court message plutôt qu'une grille fausse : les listes n'ont de sens que
    // construites sur le catalogue. S'il est définitivement indisponible (fichier partagé absent du
    // thème), on le dit et on donne le téléphone — même repli que le calendrier de rendez-vous.
    function renderPneuAttente(onDone) {
      body.innerHTML =
        renderPneuChrome(1, 'Largeur', null) +
        '<p class="mauto-devis-loading">Chargement des dimensions disponibles…</p>';
      wirePneuChrome(null, onDone);
    }

    function renderPneuIndisponible(onDone) {
      body.innerHTML =
        renderPneuChrome(1, 'Largeur', null) +
        '<p class="mauto-devis-error">Les dimensions n\'ont pas pu être chargées. Appelez-nous au ' +
        '<a href="tel:+33240268661">02 40 26 86 61</a>, nous trouvons votre pneu avec vous.</p>';
      wirePneuChrome(null, onDone);
    }

    function renderPneuStepLargeur(onDone) {
      if (!window.MautoPneuDimensions) { renderPneuIndisponible(onDone); return; }
      body.innerHTML =
        renderPneuChrome(1, 'Largeur', null) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">' + icone('info') + 'Où trouver mes dimensions ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>La largeur, la hauteur et le diamètre sont inscrits sur le flanc du pneu (ex&nbsp;: 205/55 R16) ou dans le carnet d\'entretien du véhicule.</p>' +
        renderPneuGrille('largeur', 'largeur', 'Largeurs disponibles chez nous', 'Toutes les largeurs', 'Voir toutes les largeurs');
      wirePneuChrome(null, onDone);
      wirePneuGrille('largeur', 'largeur',
        function () { renderPneuStepHauteur(onDone); },
        function () { renderPneuStepLargeur(onDone); });
    }

    function renderPneuStepHauteur(onDone) {
      body.innerHTML =
        renderPneuChrome(2, 'Hauteur', function () { renderPneuStepLargeur(onDone); }) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">' + icone('info') + 'Où trouver mes dimensions ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>La hauteur (en % de la largeur) est le deuxième nombre inscrit sur le flanc du pneu (ex&nbsp;: 205/<strong>55</strong> R16).</p>' +
        renderPneuGrille('hauteur', 'hauteur',
          'Hauteurs disponibles en ' + esc(state.pneu.largeur), 'Toutes les hauteurs', 'Voir toutes les hauteurs');
      wirePneuChrome(function () { renderPneuStepLargeur(onDone); }, onDone);
      wirePneuGrille('hauteur', 'hauteur',
        function () { renderPneuStepDiametre(onDone); },
        function () { renderPneuStepHauteur(onDone); });
    }

    function renderPneuStepDiametre(onDone) {
      body.innerHTML =
        renderPneuChrome(3, 'Diamètre', function () { renderPneuStepHauteur(onDone); }) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">' + icone('info') + 'Où trouver mes dimensions ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>Le diamètre (en pouces) est inscrit après le "R" sur le flanc du pneu (ex&nbsp;: 205/55 R<strong>16</strong>) — c\'est aussi le diamètre de la jante.</p>' +
        renderPneuGrille('diametre', 'diametre',
          'Diamètres disponibles en ' + esc(state.pneu.largeur) + '/' + esc(state.pneu.hauteur),
          'Tous les diamètres', 'Voir tous les diamètres');
      wirePneuChrome(function () { renderPneuStepHauteur(onDone); }, onDone);
      wirePneuGrille('diametre', 'diametre',
        function () { renderPneuStepCharge(onDone); },
        function () { renderPneuStepDiametre(onDone); });
    }

    function dimensionLisible() {
      return esc(state.pneu.largeur) + '/' + esc(state.pneu.hauteur) + ' R' + esc(state.pneu.diametre);
    }

    function renderPneuIndiceStep(n, title, champ, field, nextFn, backFn, rerender, labelDispo, labelToutes, lienLabel, guideTitle, guideBody, riskList, onDone) {
      body.innerHTML =
        renderPneuChrome(n, title, backFn) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">' + icone('info') + 'Où trouver mes dimensions ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>Cette information est inscrite sur le flanc du pneu juste après la dimension (ex&nbsp;: 205/55 R16 <strong>91V</strong>) ou dans le carnet d\'entretien.</p>' +
        renderPneuGrille(champ, field, labelDispo, labelToutes, lienLabel) +
        '<button type="button" class="mauto-pneu-link" data-action="pneu-ne-sait-pas">Je ne sais pas</button>' +
        '<div class="mauto-pneu-guide">' +
          '<button type="button" class="mauto-pneu-guide-toggle" data-action="pneu-guide-toggle">Guide d\'achat <span>▾</span></button>' +
          '<div class="mauto-pneu-guide-body" hidden>' +
            '<p><strong>' + esc(guideTitle) + '</strong></p><p>' + guideBody + '</p>' +
            '<p><strong>Risque en cas d\'erreur&nbsp;:</strong></p>' +
            '<ul>' + riskList.map(function (r) { return '<li>' + esc(r) + '</li>'; }).join('') + '</ul>' +
          '</div>' +
        '</div>';
      wirePneuChrome(backFn, onDone);
      wirePneuGrille(champ, field, nextFn, rerender);
      body.querySelector('[data-action="pneu-ne-sait-pas"]').addEventListener('click', function () {
        state.pneu[field] = NE_SAIT_PAS;
        nextFn();
      });
      body.querySelector('[data-action="pneu-guide-toggle"]').addEventListener('click', function () {
        var g = body.querySelector('.mauto-pneu-guide-body');
        g.hidden = !g.hidden;
      });
    }

    function renderPneuStepCharge(onDone) {
      renderPneuIndiceStep(4, 'Indice de charge', 'charge', 'indiceCharge',
        function () { renderPneuStepVitesse(onDone); },
        function () { renderPneuStepDiametre(onDone); },
        function () { renderPneuStepCharge(onDone); },
        'Indices de charge disponibles en ' + dimensionLisible(),
        'Tous les indices de charge', 'Voir tous les indices de charge',
        'Indice de charge',
        'Plus il est élevé, plus la charge maximale supportée augmente. Il est obligatoire d\'utiliser des pneus dont l\'indice est égal ou supérieur aux préconisations constructeur (carnet d\'entretien). Vous pouvez monter d\'un ou plusieurs indices, mais jamais en dessous de la préconisation.',
        ['Risque de déjantage', 'Risque d\'éclatement du pneumatique'], onDone);
    }

    function renderPneuStepVitesse(onDone) {
      renderPneuIndiceStep(5, 'Indice de vitesse', 'vitesse', 'indiceVitesse',
        function () { renderPneuStepSaison(onDone); },
        function () { renderPneuStepCharge(onDone); },
        function () { renderPneuStepVitesse(onDone); },
        'Indices de vitesse disponibles en ' + dimensionLisible(),
        'Tous les indices de vitesse', 'Voir tous les indices de vitesse',
        'Indice de vitesse',
        'Il indique la vitesse maximale supportée par le pneu. Comme pour l\'indice de charge, il ne doit jamais être inférieur à la préconisation constructeur — vous pouvez en revanche monter en gamme.',
        ['Perte d\'adhérence', 'Risque d\'éclatement suite à une surchauffe', 'Véhicule refusé au contrôle technique'], onDone);
    }

    function renderPneuStepSaison(onDone) {
      var qte = state.pneu.quantite;
      var qteIsCustom = !!qte && qte !== '2' && qte !== '4';
      var qteCustomVisible = qteIsCustom || state._qteAutreOpen;
      var saisons = saisonsProposees();
      body.innerHTML =
        renderPneuChrome(6, 'Paramètres', function () { renderPneuStepVitesse(onDone); }) +
        '<button type="button" class="mauto-pneu-info-pill" data-action="pneu-info">' + icone('info') + 'Quels pneus pour quelle saison ?</button>' +
        '<p class="mauto-pneu-info-text" hidden>' + saisons.map(function (s) { return SAISON_EXPLICATIONS[s.key]; }).filter(Boolean).join(' ') + '</p>' +
        '<p class="mauto-devis-field-label">Sélectionnez la saison de vos pneus</p>' +
        '<div class="mauto-pneu-saison-grid">' +
          saisons.map(function (s) {
            return '<button type="button" class="mauto-pneu-saison' + (state.pneu.saison === s.key ? ' is-selected' : '') + '" data-value="' + s.key + '">' +
              '<span class="mauto-pneu-saison-icon">' + icone(s.key) + '</span><span>' + esc(s.label) + '</span></button>';
          }).join('') +
        '</div>' +
        '<p class="mauto-devis-field-label">Nombre de pneus à changer</p>' +
        '<div class="mauto-pneu-grid">' +
          '<button type="button" class="mauto-pneu-option' + (qte === '2' ? ' is-selected' : '') + '" data-qte="2">2 pneus</button>' +
          '<button type="button" class="mauto-pneu-option' + (qte === '4' ? ' is-selected' : '') + '" data-qte="4">4 pneus</button>' +
          '<button type="button" class="mauto-pneu-option' + (qteCustomVisible ? ' is-selected' : '') + '" data-qte="autre">Autre</button>' +
        '</div>' +
        (qteCustomVisible ?
          '<div class="mauto-devis-field mauto-pneu-qte-custom">' +
            '<input type="number" min="1" max="8" inputmode="numeric" placeholder="Nombre de pneus" value="' + (qteIsCustom ? esc(qte) : '') + '">' +
          '</div>'
        : '') +
        '<p class="mauto-devis-field-label">Spécificité véhicule</p>' +
        '<label class="mauto-pneu-toggle-row">' +
          '<span>Véhicule électrique</span>' +
          '<input type="checkbox" class="mauto-pneu-ve-toggle"' + (state.pneu.vehiculeElectrique ? ' checked' : '') + '>' +
        '</label>' +
        '<div class="mauto-devis-actions mauto-pneu-actions-stacked">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-secondary" data-action="pneu-skip"' + (state.pneu.quantite ? '' : ' disabled') + '>Passer cette étape</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="pneu-valider"' + (state.pneu.saison && state.pneu.quantite ? '' : ' disabled') + '>Valider</button>' +
        '</div>';
      wirePneuChrome(function () { renderPneuStepVitesse(onDone); }, onDone);
      var validerBtn = body.querySelector('[data-action="pneu-valider"]');
      var skipBtn = body.querySelector('[data-action="pneu-skip"]');
      var saisonBtns = body.querySelectorAll('.mauto-pneu-saison');
      for (var i = 0; i < saisonBtns.length; i++) {
        saisonBtns[i].addEventListener('click', function (ev) {
          state.pneu.saison = ev.currentTarget.getAttribute('data-value');
          var all = body.querySelectorAll('.mauto-pneu-saison');
          for (var j = 0; j < all.length; j++) all[j].classList.toggle('is-selected', all[j] === ev.currentTarget);
          validerBtn.disabled = !state.pneu.quantite;
        });
      }
      var qteBtns = body.querySelectorAll('[data-qte]');
      for (var k = 0; k < qteBtns.length; k++) {
        qteBtns[k].addEventListener('click', function (ev) {
          var v = ev.currentTarget.getAttribute('data-qte');
          if (v === 'autre') {
            state._qteAutreOpen = true;
            state.pneu.quantite = '';
          } else {
            state._qteAutreOpen = false;
            state.pneu.quantite = v;
          }
          renderPneuStepSaison(onDone);
        });
      }
      var qteCustomInput = body.querySelector('.mauto-pneu-qte-custom input');
      if (qteCustomInput) {
        qteCustomInput.focus();
        qteCustomInput.addEventListener('input', function (ev) {
          state.pneu.quantite = ev.target.value.trim();
          skipBtn.disabled = !state.pneu.quantite;
          validerBtn.disabled = !(state.pneu.saison && state.pneu.quantite);
        });
      }
      body.querySelector('.mauto-pneu-ve-toggle').addEventListener('change', function (ev) {
        state.pneu.vehiculeElectrique = ev.target.checked;
      });
      skipBtn.addEventListener('click', function () { endPneuFlow(onDone); });
      validerBtn.addEventListener('click', function () { endPneuFlow(onDone); });
    }

    function renderDropdownStep() {
      setStep(1, null);
      var keys = isFamily ? FAMILIES[presetKey] : GENERAL_LIST_KEYS.concat([PB_KEY, AUTRE_KEY]);
      var options = '<option value="">— Choisissez une prestation —</option>';
      keys.forEach(function (k) {
        var p = state.pricing[k];
        if (!p) return;
        options += '<option value="' + esc(k) + '">' + esc(p.label) + '</option>';
      });
      body.innerHTML =
        '<div class="mauto-devis-field">' +
          '<label for="mauto-devis-prestation">Quelle prestation vous intéresse ?</label>' +
          '<select id="mauto-devis-prestation" class="mauto-devis-select">' + options + '</select>' +
        '</div>';
      var select = body.querySelector('#mauto-devis-prestation');
      if (state.prestationKey) select.value = state.prestationKey;
      // Sur desktop, les flèches clavier (select focus + fermé) ou la molette au survol
      // déclenchent un "change" à CHAQUE incrément, pas seulement au choix final — sans ce
      // léger débounce, l'assistant avance dès la première valeur traversée (ex. l'option
      // juste après le choix précédent) et l'utilisateur n'atteint jamais "Pneus (achat et
      // pose)" en descendant la liste. Le picker natif mobile ne déclenche qu'un seul "change"
      // final, d'où l'absence du bug sur mobile (cf. bug rapporté 2026-08-24). 180ms est
      // imperceptible pour un clic/sélection normale mais absorbe une rafale clavier/molette.
      var prestationChangeTimer = null;
      select.addEventListener('change', function () {
        clearTimeout(prestationChangeTimer);
        prestationChangeTimer = setTimeout(function () {
          if (select.value) selectSuggestion(select.value);
        }, 180);
      });
    }

    // Barre de recherche + autocomplétion + CTA "Faire un devis" (variante hero uniquement).
    function renderSearchStep() {
      // Sur mobile, tant que le visiteur n'a pas cliqué le bouton compact, le bloc hero garde
      // son affichage réduit — pas de plein écran, rien à faire ici avant ce clic.
      if (variant === 'hero' && !heroEntered && window.matchMedia('(max-width: 768px)').matches) {
        setStep(1, null);
        body.innerHTML = '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary mauto-devis-hero-start-btn">Choisir votre prestation</button>';
        body.querySelector('.mauto-devis-hero-start-btn').addEventListener('click', function () {
          heroEntered = true;
          setFlowActive(true);
          renderSearchStep();
        });
        return;
      }

      // Desktop (ou mobile jamais "entré", cf. return ci-dessus) : bloc hero pré-flux inline.
      // Mobile déjà "entré" une fois : reste en plein écran même en revenant à cette étape.
      setFlowActive(heroEntered);
      setStep(1, null);
      body.innerHTML =
        '<div class="mauto-devis-searchbar">' +
          '<div class="mauto-devis-search-wrap">' +
            '<input type="text" class="mauto-devis-search-input" placeholder="Votre prestation…" autocomplete="off">' +
            '<div class="mauto-devis-suggestions" hidden></div>' +
          '</div>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary mauto-devis-search-btn">Faire un devis</button>' +
        '</div>' +
        '<p class="mauto-devis-error mauto-devis-search-error" hidden></p>';

      var input = body.querySelector('.mauto-devis-search-input');
      var list = body.querySelector('.mauto-devis-suggestions');
      var errorEl = body.querySelector('.mauto-devis-search-error');
      var suggestions = buildSuggestionList();

      function showSuggestions(query) {
        var q = normalize(query);
        var matches = q
          ? suggestions.filter(function (s) { return normalize(s.label).indexOf(q) !== -1; })
          : suggestions;
        if (!matches.length) { list.hidden = true; list.innerHTML = ''; return; }
        // Pas de troncature : la liste ne compte qu'une vingtaine d'entrées et le conteneur
        // défile déjà (.mauto-devis-suggestions, max-height + overflow-y:auto) — un plafond à 8
        // masquait des prestations (ex. "Pneus") tant que le client n'avait pas encore tapé assez
        // de lettres pour les faire remonter dans les résultats filtrés.
        list.innerHTML = matches.map(function (s) {
          return '<button type="button" class="mauto-devis-suggestion" data-key="' + esc(s.key) + '">' + esc(s.label) + '</button>';
        }).join('');
        list.hidden = false;
        var items = list.querySelectorAll('.mauto-devis-suggestion');
        for (var i = 0; i < items.length; i++) {
          items[i].addEventListener('click', function (ev) {
            var key = ev.currentTarget.getAttribute('data-key');
            input.value = ev.currentTarget.textContent;
            list.hidden = true;
            errorEl.hidden = true;
            selectSuggestion(key);
          });
        }
      }

      function trySubmit() {
        var q = normalize(input.value);
        if (!q) {
          errorEl.textContent = 'Merci de saisir une prestation.';
          errorEl.hidden = false;
          return;
        }
        var match = suggestions.filter(function (s) { return normalize(s.label).indexOf(q) !== -1; })[0];
        if (!match) {
          errorEl.textContent = 'Prestation non reconnue — choisissez-la dans la liste suggérée.';
          errorEl.hidden = false;
          return;
        }
        errorEl.hidden = true;
        list.hidden = true;
        selectSuggestion(match.key);
      }

      input.addEventListener('input', function () { showSuggestions(input.value); });
      input.addEventListener('focus', function () { showSuggestions(input.value); });
      input.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter') { ev.preventDefault(); trySubmit(); }
      });
      document.addEventListener('click', function (ev) {
        if (!body.contains(ev.target)) list.hidden = true;
      });
      body.querySelector('.mauto-devis-search-btn').addEventListener('click', trySubmit);
    }

    function renderStep1() {
      if (useSearchBar) renderSearchStep();
      else renderDropdownStep();
    }

    // "Autre (précisez)" : champ libre obligatoire affiché immédiatement à l'étape 1,
    // avant même de passer à l'étape Véhicule.
    function renderAutreStep() {
      setFlowActive(true); // décrire "Autre" = prestation déjà "choisie", plein écran mobile
      setStep(1, renderStep1);
      body.innerHTML =
        '<p class="mauto-devis-field-label">Décrivez la prestation souhaitée :</p>' +
        '<div class="mauto-devis-field">' +
          '<textarea id="mauto-devis-autre-desc" rows="3" placeholder="Ex. bruit suspect au freinage, voyant moteur allumé…" required>' + esc(state.autreDescription) + '</textarea>' +
        '</div>' +
        '<p class="mauto-devis-error mauto-devis-autre-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="next">Continuer</button>' +
        '</div>';
      body.querySelector('[data-action="back"]').addEventListener('click', renderStep1);
      body.querySelector('[data-action="next"]').addEventListener('click', function () {
        var desc = body.querySelector('#mauto-devis-autre-desc').value.trim();
        var errorEl = body.querySelector('.mauto-devis-autre-error');
        if (!desc) {
          errorEl.textContent = 'Merci de décrire la prestation souhaitée.';
          errorEl.hidden = false;
          return;
        }
        errorEl.hidden = true;
        state.autreDescription = desc;
        state.prestationKey = AUTRE_KEY;
        goBackFromVehicule = renderAutreStep;
        goToVehiculeOrEstimation();
      });
    }


    // ─── Parcours « Prendre rendez-vous — pare-brise » ──────────────────────────────────────
    // Six étapes calquées sur les parcours des réseaux de vitrage : vitrage → dommage →
    // véhicule → coordonnées → rendez-vous → récapitulatif, puis écran de confirmation.
    // Aucune API nouvelle : la demande part sur /api/devis avec la clé « autre », toutes les
    // réponses étant recopiées dans le message (seul champ libre conservé par le sync-server).
    var PB_ETAPES = 6;
    var PB_DELAI_OUVRE = 4; // jours ouvrés minimum : délai de commande du vitrage

    var PB_SVG = {
      pareBrise:
        '<svg class="mauto-pb-illu" viewBox="0 0 120 150" aria-hidden="true">' +
          '<rect x="22" y="10" width="76" height="130" rx="26" fill="#eef1f4" stroke="#cbd5e1" stroke-width="2"/>' +
          '<path d="M38 34h44l7 22H31z" fill="#f5c400" stroke="#b98a00" stroke-width="2" stroke-linejoin="round"/>' +
          '<rect x="36" y="62" width="48" height="34" rx="6" fill="#dde3ea"/>' +
          '<path d="M36 110h48l-5 16H41z" fill="#dde3ea"/>' +
        '</svg>',
      lunette:
        '<svg class="mauto-pb-illu" viewBox="0 0 120 150" aria-hidden="true">' +
          '<rect x="22" y="10" width="76" height="130" rx="26" fill="#eef1f4" stroke="#cbd5e1" stroke-width="2"/>' +
          '<path d="M38 34h44l7 22H31z" fill="#dde3ea"/>' +
          '<rect x="36" y="62" width="48" height="34" rx="6" fill="#dde3ea"/>' +
          '<path d="M36 110h48l-5 16H41z" fill="#f5c400" stroke="#b98a00" stroke-width="2" stroke-linejoin="round"/>' +
        '</svg>',
      laterales:
        '<svg class="mauto-pb-illu" viewBox="0 0 120 150" aria-hidden="true">' +
          '<rect x="22" y="10" width="76" height="130" rx="26" fill="#eef1f4" stroke="#cbd5e1" stroke-width="2"/>' +
          '<path d="M38 34h44l7 22H31z" fill="#dde3ea"/>' +
          '<rect x="36" y="62" width="48" height="34" rx="6" fill="#dde3ea"/>' +
          '<path d="M36 110h48l-5 16H41z" fill="#dde3ea"/>' +
          '<rect x="14" y="58" width="10" height="42" rx="4" fill="#f5c400" stroke="#b98a00" stroke-width="2"/>' +
          '<rect x="96" y="58" width="10" height="42" rx="4" fill="#f5c400" stroke="#b98a00" stroke-width="2"/>' +
        '</svg>',
      fissure:
        '<svg class="mauto-pb-illu" viewBox="0 0 150 90" aria-hidden="true">' +
          '<path d="M6 76C24 24 126 24 144 76z" fill="#f1f3f5" stroke="#cbd5e1" stroke-width="2"/>' +
          '<path d="M44 66l18-18-9-6 22-14-8 13 12 4-14 12 19 3" fill="none" stroke="#111" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"/>' +
        '</svg>',
      impacts:
        '<svg class="mauto-pb-illu" viewBox="0 0 150 90" aria-hidden="true">' +
          '<path d="M6 76C24 24 126 24 144 76z" fill="#f1f3f5" stroke="#cbd5e1" stroke-width="2"/>' +
          '<g stroke="#111" stroke-width="2.5" stroke-linecap="round">' +
            '<path d="M42 50l-8-7M42 50l9-6M42 50l-5 9M42 50l7 8"/><circle cx="42" cy="50" r="3" fill="#111" stroke="none"/>' +
            '<path d="M78 42l-7-6M78 42l8-5M78 42l-4 8M78 42l6 7"/><circle cx="78" cy="42" r="3" fill="#111" stroke="none"/>' +
            '<path d="M108 58l-7-6M108 58l8-5M108 58l-4 8"/><circle cx="108" cy="58" r="3" fill="#111" stroke="none"/>' +
          '</g>' +
        '</svg>',
      impact:
        '<svg class="mauto-pb-illu" viewBox="0 0 150 90" aria-hidden="true">' +
          '<path d="M6 76C24 24 126 24 144 76z" fill="#f1f3f5" stroke="#cbd5e1" stroke-width="2"/>' +
          '<g stroke="#111" stroke-width="3" stroke-linecap="round">' +
            '<path d="M75 52l-14-11M75 52l15-10M75 52l-8 15M75 52l12 14M75 52l-18 6M75 52l19 4"/>' +
            '<circle cx="75" cy="52" r="5" fill="#111" stroke="none"/>' +
          '</g>' +
        '</svg>'
    };

    var PB_AUTRES = ['Optique de phare', 'Glace de rétroviseur', 'Vitre de custode', 'Autre vitrage'];

    // ── Dates : premier rendez-vous possible à J+4 ouvrés, week-ends exclus ────────────────
    function pbAjouteJoursOuvres(d, n) {
      var r = new Date(d.getTime());
      var reste = n;
      while (reste > 0) {
        r.setDate(r.getDate() + 1);
        var jour = r.getDay();
        if (jour !== 0 && jour !== 6) reste--;
      }
      return r;
    }
    function pbISO(d) {
      var m = String(d.getMonth() + 1), j = String(d.getDate());
      return d.getFullYear() + '-' + (m.length < 2 ? '0' + m : m) + '-' + (j.length < 2 ? '0' + j : j);
    }
    function pbDateLisible(iso) {
      if (!iso) return '';
      var p = iso.split('-');
      var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
      var jours = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
      var mois = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
      return jours[d.getDay()] + ' ' + d.getDate() + ' ' + mois[d.getMonth()] + ' ' + d.getFullYear();
    }

    // ── Chrome commun aux six étapes ───────────────────────────────────────────────────────
    function pbChrome(n, titre, backHandler) {
      var pct = Math.round((n / PB_ETAPES) * 100);
      return (
        '<div class="mauto-pneu-header">' +
          (backHandler ? '<button type="button" class="mauto-pneu-back" data-action="pb-back" aria-label="Retour"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg></button>' : '<span class="mauto-pneu-back-spacer"></span>') +
          '<span class="mauto-pneu-title">Prendre rendez-vous</span>' +
          '<button type="button" class="mauto-pneu-close" data-action="pb-close" aria-label="Fermer">✕</button>' +
        '</div>' +
        '<div class="mauto-pneu-progress-row"><span>' + esc(titre) + '</span><span>' + n + '/' + PB_ETAPES + '</span></div>' +
        '<div class="mauto-pneu-progressbar"><div style="width:' + pct + '%"></div></div>'
      );
    }

    function pbWire(backHandler) {
      if (window.innerWidth <= 768) { body.scrollTop = 0; void body.offsetHeight; }
      var b = body.querySelector('[data-action="pb-back"]');
      if (b && backHandler) b.addEventListener('click', backHandler);
      var c = body.querySelector('[data-action="pb-close"]');
      if (c) c.addEventListener('click', pbQuitte);
    }

    var pbRetourInitial = null;
    function startPareBriseFlow(back) {
      pbRetourInitial = back || null;
      // Le parcours a sa propre barre de progression 1/6 : on masque celle du devis générique,
      // comme le fait le tiroir pneu, pour ne pas afficher deux fils d ariane contradictoires.
      mobileTopbar.style.display = "none";
      stepsBar.style.display = "none";
      if (quickPicksBar) quickPicksBar.style.display = "none";
      renderPbVitrage();
    }

    function pbQuitte() {
      mobileTopbar.style.display = "";
      stepsBar.style.display = "";
      if (quickPicksBar) quickPicksBar.style.display = "";
      setFlowActive(false);
      state.prestationKey = "";
      renderStep1();
    }

    // ── 1/6 — Vitrage concerné ─────────────────────────────────────────────────────────────
    function renderPbVitrage() {
      setFlowActive(true);
      setStep(1, pbRetourInitial);
      var cartes = [
        { v: 'Pare-brise', l: 'Pare-brise', svg: PB_SVG.pareBrise },
        { v: 'Lunette arrière', l: 'Lunette arrière', svg: PB_SVG.lunette },
        { v: 'Vitres latérales', l: 'Vitres latérales', svg: PB_SVG.laterales }
      ];
      body.innerHTML =
        pbChrome(1, 'Le vitrage concerné', pbRetourInitial) +
        '<p class="mauto-pb-question">Sélectionnez le vitrage concerné</p>' +
        '<div class="mauto-pb-illus">' +
          cartes.map(function (c) {
            return '<button type="button" class="mauto-pb-illu-card' + (state.pb.vitrage === c.v ? ' is-selected' : '') + '" data-value="' + esc(c.v) + '">' +
              c.svg + '<span class="mauto-pb-illu-label">' + esc(c.l) + '</span></button>';
          }).join('') +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-pb-autre">Autre prestation de vitrage</label>' +
          '<select id="mauto-pb-autre" class="mauto-devis-select">' +
            '<option value="">— Autres prestations —</option>' +
            PB_AUTRES.map(function (a) {
              return '<option value="' + esc(a) + '"' + (state.pb.vitrage === a ? ' selected' : '') + '>' + esc(a) + '</option>';
            }).join('') +
          '</select>' +
        '</div>' +
        '<p class="mauto-pb-hamon">Votre assureur ne peut pas vous imposer son garage : la <strong>loi Hamon</strong> vous laisse le libre choix du réparateur, sans réduire votre prise en charge.</p>';
      pbWire(pbRetourInitial);
      var cards = body.querySelectorAll('.mauto-pb-illu-card');
      for (var k = 0; k < cards.length; k++) {
        cards[k].addEventListener('click', function (ev) {
          state.pb.vitrage = ev.currentTarget.getAttribute('data-value');
          if (state.pb.vitrage === 'Pare-brise') renderPbDommage();
          else renderPbVehicule();
        });
      }
      body.querySelector('#mauto-pb-autre').addEventListener('change', function (ev) {
        if (!ev.target.value) return;
        state.pb.vitrage = ev.target.value;
        renderPbVehicule();
      });
    }

    // ── 2/6 — Diagnostic du pare-brise ─────────────────────────────────────────────────────
    function renderPbDommage() {
      setFlowActive(true);
      setStep(2, renderPbVitrage);
      var cartes = [
        { v: 'Fissure', l: 'Fissure', svg: PB_SVG.fissure },
        { v: 'Nombreux impacts', l: 'Nombreux impacts', svg: PB_SVG.impacts },
        { v: 'Un impact', l: 'Un impact', svg: PB_SVG.impact }
      ];
      body.innerHTML =
        pbChrome(2, 'Pare-brise : diagnostic', renderPbVitrage) +
        '<p class="mauto-pb-question">Décrivez le type de dommage</p>' +
        '<div class="mauto-pb-illus">' +
          cartes.map(function (c) {
            return '<button type="button" class="mauto-pb-illu-card' + (state.pb.dommage === c.v ? ' is-selected' : '') + '" data-value="' + esc(c.v) + '">' +
              c.svg + '<span class="mauto-pb-illu-label">' + esc(c.l) + '</span></button>';
          }).join('') +
        '</div>' +
        '<p class="mauto-devis-field-label">Y a-t-il une caméra derrière le pare-brise, en haut au centre ?</p>' +
        '<div class="mauto-pb-choices mauto-pb-choices--row">' +
          ['Oui', 'Non', 'Je ne sais pas'].map(function (v) {
            return '<button type="button" class="mauto-pb-choice' + (state.pb.adas === v ? ' is-selected' : '') + '" data-adas="' + esc(v) + '"><span class="mauto-pb-choice-label">' + esc(v) + '</span></button>';
          }).join('') +
        '</div>' +
        '<p class="mauto-pb-info">Nous remplaçons le vitrage et recalibrons la caméra dans notre atelier. Nous ne pratiquons pas la réparation d\'impact à la résine.</p>' +
        '<p class="mauto-devis-error mauto-pb-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="pb-prev">← Retour</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="pb-next">Suivant</button>' +
        '</div>';
      pbWire(renderPbVitrage);
      var cards = body.querySelectorAll('.mauto-pb-illu-card');
      for (var k = 0; k < cards.length; k++) {
        cards[k].addEventListener('click', function (ev) {
          state.pb.dommage = ev.currentTarget.getAttribute('data-value');
          for (var m = 0; m < cards.length; m++) cards[m].classList.remove('is-selected');
          ev.currentTarget.classList.add('is-selected');
          body.querySelector('.mauto-pb-error').hidden = true;
        });
      }
      var adas = body.querySelectorAll('[data-adas]');
      for (var a = 0; a < adas.length; a++) {
        adas[a].addEventListener('click', function (ev) {
          state.pb.adas = ev.currentTarget.getAttribute('data-adas');
          for (var m = 0; m < adas.length; m++) adas[m].classList.remove('is-selected');
          ev.currentTarget.classList.add('is-selected');
        });
      }
      body.querySelector('[data-action="pb-prev"]').addEventListener('click', renderPbVitrage);
      body.querySelector('[data-action="pb-next"]').addEventListener('click', function () {
        if (!state.pb.dommage) {
          var e = body.querySelector('.mauto-pb-error');
          e.textContent = 'Merci d\'indiquer le type de dommage.';
          e.hidden = false;
          return;
        }
        renderPbVehicule();
      });
    }

    // ── 3/6 — Véhicule ─────────────────────────────────────────────────────────────────────
    function renderPbVehicule() {
      setFlowActive(true);
      var back = state.pb.vitrage === 'Pare-brise' ? renderPbDommage : renderPbVitrage;
      setStep(3, back);
      body.innerHTML =
        pbChrome(3, 'Votre véhicule', back) +
        '<p class="mauto-pb-question">Renseignez l\'immatriculation, la marque et le modèle</p>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-pb-immat">Immatriculation *</label>' +
          '<input type="text" id="mauto-pb-immat" placeholder="Ex. AB-123-CD" value="' + esc(state.vehicule.immatriculation) + '">' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-pb-marque">Marque *</label>' +
          '<input type="text" id="mauto-pb-marque" placeholder="Ex. Peugeot" value="' + esc(state.vehicule.marque) + '">' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-pb-modele">Modèle *</label>' +
          '<input type="text" id="mauto-pb-modele" placeholder="Ex. 208" value="' + esc(state.vehicule.modele) + '">' +
        '</div>' +
        '<p class="mauto-devis-error mauto-pb-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="pb-prev">← Retour</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="pb-next">Suivant</button>' +
        '</div>';
      pbWire(back);
      body.querySelector('[data-action="pb-prev"]').addEventListener('click', back);
      body.querySelector('[data-action="pb-next"]').addEventListener('click', function () {
        var immat = body.querySelector('#mauto-pb-immat').value.trim();
        var marque = body.querySelector('#mauto-pb-marque').value.trim();
        var modele = body.querySelector('#mauto-pb-modele').value.trim();
        var e = body.querySelector('.mauto-pb-error');
        if (!immat || !marque || !modele) {
          e.textContent = 'Merci de renseigner l\'immatriculation, la marque et le modèle.';
          e.hidden = false;
          return;
        }
        if (!IMMAT_REGEX.test(immat)) {
          e.textContent = 'Format d\'immatriculation invalide — utilisez le format AB-123-CD.';
          e.hidden = false;
          return;
        }
        state.vehicule = { immatriculation: normalizeImmat(immat), marque: marque, modele: modele };
        renderPbCoordonnees();
      });
    }

    // ── 4/6 — Coordonnées + garantie bris de glace ─────────────────────────────────────────
    function renderPbCoordonnees() {
      setFlowActive(true);
      setStep(4, renderPbVehicule);
      body.innerHTML =
        pbChrome(4, 'Vos coordonnées', renderPbVehicule) +
        '<p class="mauto-pb-question">Renseignez vos coordonnées</p>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-pb-prenom">Prénom *</label>' +
          '<input type="text" id="mauto-pb-prenom" value="' + esc(state.pb.prenom) + '">' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-pb-nom">Nom *</label>' +
          '<input type="text" id="mauto-pb-nom" value="' + esc(state.pb.nom) + '">' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-pb-tel">Téléphone *</label>' +
          '<input type="tel" id="mauto-pb-tel" value="' + esc(state.coordonnees.telephone) + '">' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-pb-email">Email *</label>' +
          '<input type="email" id="mauto-pb-email" value="' + esc(state.coordonnees.email) + '">' +
        '</div>' +
        '<p class="mauto-devis-field-label">Êtes-vous assuré en bris de glace ?<br><span class="mauto-pb-souslabel">Toutes assurances acceptées</span></p>' +
        '<div class="mauto-pb-choices mauto-pb-choices--row">' +
          ['Oui', 'Non', 'Je ne sais pas'].map(function (v) {
            return '<button type="button" class="mauto-pb-choice' + (state.pb.brisGlace === v ? ' is-selected' : '') + '" data-bg="' + esc(v) + '"><span class="mauto-pb-choice-label">' + esc(v) + '</span></button>';
          }).join('') +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-pb-assureur">Votre assurance (facultatif)</label>' +
          '<input type="text" id="mauto-pb-assureur" placeholder="Ex. MAIF, AXA, Groupama…" value="' + esc(state.pb.assureur) + '">' +
        '</div>' +
        '<div class="mauto-pb-facultatif">' +
          '<p class="mauto-pb-facultatif-titre">Informations facultatives</p>' +
          '<div class="mauto-devis-field">' +
            '<label for="mauto-pb-note">Précisions sur le dégât, le véhicule, vos contraintes…</label>' +
            '<textarea id="mauto-pb-note" rows="3" placeholder="Ex. impact côté conducteur, véhicule équipé d\'un affichage tête haute…">' + esc(state.pb.note) + '</textarea>' +
          '</div>' +
          '<p class="mauto-pb-facultatif-photos">Trois photos nous font gagner du temps : le <strong>vitrage en entier</strong>, votre <strong>carte grise</strong> et votre <strong>attestation d\'assurance</strong> — c\'est elle qui nous permet d\'envoyer le devis à votre assureur avant le rendez-vous.</p>' +
          '<input type="file" id="mauto-pb-fichiers" accept="image/*" multiple hidden>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary mauto-pb-photos-ajout" data-action="pb-photos">📷 Ajouter mes photos</button>' +
          '<p class="mauto-pb-photos-liste"></p>' +
        '</div>' +
        '<p class="mauto-devis-error mauto-pb-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="pb-prev">← Retour</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="pb-next">Suivant</button>' +
        '</div>';
      pbWire(renderPbVehicule);
      var champFichiers = body.querySelector('#mauto-pb-fichiers');
      var listeEl = body.querySelector('.mauto-pb-photos-liste');
      function afficherPhotos() {
        if (!listeEl) return;
        listeEl.innerHTML = state.pb.photos.length
          ? state.pb.photos.map(function (p, i) {
              return '<span class="mauto-pb-photo-vignette"><img src="' + p.data + '" alt=""><button type="button" data-retirer="' + i + '" aria-label="Retirer">✕</button></span>';
            }).join('')
          : '';
        var retraits = listeEl.querySelectorAll('[data-retirer]');
        for (var r = 0; r < retraits.length; r++) {
          retraits[r].addEventListener('click', function (ev) {
            state.pb.photos.splice(Number(ev.currentTarget.getAttribute('data-retirer')), 1);
            afficherPhotos();
          });
        }
      }
      afficherPhotos();
      body.querySelector('[data-action="pb-photos"]').addEventListener('click', function () { champFichiers.click(); });
      champFichiers.addEventListener('change', function () {
        var btnPhotos = body.querySelector('[data-action="pb-photos"]');
        var fichiers = Array.prototype.slice.call(champFichiers.files || []).slice(0, 4 - state.pb.photos.length);
        if (!fichiers.length) return;
        btnPhotos.disabled = true;
        btnPhotos.textContent = 'Préparation…';
        Promise.all(fichiers.map(pbCompresser)).then(function (res) {
          res.filter(Boolean).forEach(function (p) { state.pb.photos.push(p); });
          champFichiers.value = '';
          btnPhotos.disabled = false;
          btnPhotos.textContent = state.pb.photos.length ? '📷 Ajouter une autre photo' : '📷 Ajouter mes photos';
          afficherPhotos();
        });
      });

      var bg = body.querySelectorAll('[data-bg]');
      for (var i2 = 0; i2 < bg.length; i2++) {
        bg[i2].addEventListener('click', function (ev) {
          state.pb.brisGlace = ev.currentTarget.getAttribute('data-bg');
          for (var m = 0; m < bg.length; m++) bg[m].classList.remove('is-selected');
          ev.currentTarget.classList.add('is-selected');
        });
      }
      function lire() {
        state.pb.prenom = body.querySelector('#mauto-pb-prenom').value.trim();
        state.pb.nom = body.querySelector('#mauto-pb-nom').value.trim();
        state.pb.assureur = body.querySelector('#mauto-pb-assureur').value.trim();
        state.pb.note = body.querySelector('#mauto-pb-note').value.trim();
        state.coordonnees = {
          nom: (state.pb.prenom + ' ' + state.pb.nom).trim(),
          telephone: body.querySelector('#mauto-pb-tel').value.trim(),
          email: body.querySelector('#mauto-pb-email').value.trim()
        };
      }
      body.querySelector('[data-action="pb-prev"]').addEventListener('click', function () { lire(); renderPbVehicule(); });
      body.querySelector('[data-action="pb-next"]').addEventListener('click', function () {
        lire();
        var e = body.querySelector('.mauto-pb-error');
        if (!state.pb.prenom || !state.pb.nom || !state.coordonnees.telephone || !state.coordonnees.email) {
          e.textContent = 'Merci de renseigner vos prénom, nom, téléphone et email.';
          e.hidden = false;
          return;
        }
        if (!state.pb.brisGlace) {
          e.textContent = 'Merci d\'indiquer si vous êtes assuré en bris de glace.';
          e.hidden = false;
          return;
        }
        renderPbRdv();
      });
    }

    // ── 5/6 — Rendez-vous ──────────────────────────────────────────────────────────────────
    // Fermetures du garage, chargées une fois : elles grisent des jours supplémentaires dans le
    // calendrier (congés, ponts). Sans elles, on affiche quand même le calendrier — les règles
    // fixes (week-ends, fériés, délai de commande du vitrage) suffisent à cadrer le choix.
    var pbFermetures = null;

    // Photos du client : compressées dans le navigateur avant l'envoi (1600 px maxi, JPEG 78 %),
    // comme le fait l'app pour les photos de véhicules. Une photo de téléphone de 4 Mo tombe ainsi
    // autour de 300 Ko : l'envoi passe même en 4G, et le stockage du garage ne gonfle pas.
    function pbCompresser(fichier) {
      return new Promise(function (resoudre) {
        var lecteur = new FileReader();
        lecteur.onload = function () {
          var img = new Image();
          img.onload = function () {
            var max = 1600;
            var l = img.width, h = img.height;
            if (l > max || h > max) {
              if (l > h) { h = Math.round(h * max / l); l = max; }
              else { l = Math.round(l * max / h); h = max; }
            }
            var c = document.createElement('canvas');
            c.width = l; c.height = h;
            c.getContext('2d').drawImage(img, 0, 0, l, h);
            resoudre({ nom: fichier.name, type: 'image/jpeg', data: c.toDataURL('image/jpeg', 0.78) });
          };
          img.onerror = function () { resoudre(null); };
          img.src = lecteur.result;
        };
        lecteur.onerror = function () { resoudre(null); };
        lecteur.readAsDataURL(fichier);
      });
    }

    function renderPbRdv() {
      setFlowActive(true);
      setStep(5, renderPbCoordonnees);
      var CAL = window.MautoCalendrier;
      var creneaux = [
        { v: 'Dépôt la veille entre 18h et 18h30', l: 'Je dépose la veille', h: 'entre 18h et 18h30' },
        { v: 'Dépôt le matin entre 8h et 9h30', l: 'Je dépose le matin même', h: 'entre 8h et 9h30' }
      ];
      var min = CAL ? CAL.dateMinParJoursOuvres(PB_DELAI_OUVRE) : pbAjouteJoursOuvres(new Date(), PB_DELAI_OUVRE);
      var minStr = CAL ? CAL.versChaine(min) : pbISO(min);

      body.innerHTML =
        pbChrome(5, 'Mon rendez-vous', renderPbCoordonnees) +
        '<p class="mauto-pb-question">Choisissez votre jour d\'intervention</p>' +
        '<p class="mauto-pb-info">Premier rendez-vous possible dans <strong>4 jours ouvrés</strong>, le temps de commander le vitrage adapté à votre véhicule. Les jours grisés ne sont pas disponibles.</p>' +
        '<div class="mauto-devis-calendar">' +
          '<div class="mauto-devis-calendar-nav">' +
            '<button type="button" class="mauto-devis-calendar-arrow" data-action="mois-prec" aria-label="Mois précédent">‹</button>' +
            '<span class="mauto-devis-calendar-label"></span>' +
            '<button type="button" class="mauto-devis-calendar-arrow" data-action="mois-suiv" aria-label="Mois suivant">›</button>' +
          '</div>' +
          '<div class="mauto-devis-calendar-weekdays">' +
            ['L', 'M', 'M', 'J', 'V', 'S', 'D'].map(function (w) { return '<span>' + w + '</span>'; }).join('') +
          '</div>' +
          '<div class="mauto-devis-calendar-days"></div>' +
        '</div>' +
        '<p class="mauto-pb-jour-choisi" hidden></p>' +
        '<p class="mauto-devis-field-label">Comment souhaitez-vous déposer le véhicule ?</p>' +
        '<div class="mauto-pb-choices">' +
          creneaux.map(function (c) {
            return '<button type="button" class="mauto-pb-choice' + (state.pb.creneau === c.v ? ' is-selected' : '') + '" data-creneau="' + esc(c.v) + '">' +
              '<span class="mauto-pb-choice-label">' + esc(c.l) + '</span>' +
              '<span class="mauto-pb-choice-hint">' + esc(c.h) + '</span></button>';
          }).join('') +
        '</div>' +
        '<label class="mauto-pb-case"><input type="checkbox" id="mauto-pb-pret"' + (state.pb.pret === 'Oui' ? ' checked' : '') + '>' +
          '<span><strong>J\'ai besoin d\'un véhicule de prêt gratuit</strong> le temps des travaux<br>' +
          '<span class="mauto-pb-case-hint">Sur réservation, selon disponibilité. Permis et empreinte bancaire demandés au départ.</span></span></label>' +
        '<p class="mauto-pb-info mauto-pb-note-pret" hidden>Avec un véhicule de prêt, le dépôt se fait le matin du rendez-vous : c\'est à ce moment-là que nous vous remettons les clés.</p>' +
        '<p class="mauto-pb-info">Votre véhicule est immobilisé la journée, le temps que la colle sèche en sécurité.</p>' +
        '<p class="mauto-devis-error mauto-pb-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="pb-prev">← Retour</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="pb-next">Suivant</button>' +
        '</div>';
      pbWire(renderPbCoordonnees);

      var labelEl = body.querySelector('.mauto-devis-calendar-label');
      var joursEl = body.querySelector('.mauto-devis-calendar-days');
      var choisiEl = body.querySelector('.mauto-pb-jour-choisi');
      var precBtn = body.querySelector('[data-action="mois-prec"]');
      var suivBtn = body.querySelector('[data-action="mois-suiv"]');
      var MOIS = (CAL && CAL.MOIS) || ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

      var vueAnnee, vueMois;
      if (state.pb.date) {
        var p = state.pb.date.split('-');
        vueAnnee = Number(p[0]); vueMois = Number(p[1]) - 1;
      } else {
        vueAnnee = min.getFullYear(); vueMois = min.getMonth();
      }

      function selectionnable(d) {
        if (CAL) return CAL.estSelectionnable(d, min, pbFermetures || []);
        var jour = d.getDay();
        return d >= min && jour !== 0 && jour !== 6;
      }

      function dessinerMois() {
        labelEl.textContent = MOIS[vueMois] + ' ' + vueAnnee;
        precBtn.disabled = (vueAnnee === min.getFullYear() && vueMois === min.getMonth());
        var premier = new Date(vueAnnee, vueMois, 1);
        var decalage = (premier.getDay() + 6) % 7;
        var nbJours = new Date(vueAnnee, vueMois + 1, 0).getDate();
        var html = '';
        for (var k = 0; k < decalage; k++) html += '<span class="mauto-devis-calendar-day is-empty"></span>';
        for (var jour = 1; jour <= nbJours; jour++) {
          var d = new Date(vueAnnee, vueMois, jour);
          var chaine = CAL ? CAL.versChaine(d) : pbISO(d);
          var ok = selectionnable(d);
          html += '<button type="button" class="mauto-devis-calendar-day' + (ok ? '' : ' is-disabled') +
            (state.pb.date === chaine ? ' is-selected' : '') + '"' +
            (ok ? ' data-date="' + chaine + '"' : ' disabled') + '>' + jour + '</button>';
        }
        joursEl.innerHTML = html;
        var btns = joursEl.querySelectorAll('.mauto-devis-calendar-day[data-date]');
        for (var b = 0; b < btns.length; b++) {
          btns[b].addEventListener('click', function (ev) {
            state.pb.date = ev.currentTarget.getAttribute('data-date');
            body.querySelector('.mauto-pb-error').hidden = true;
            dessinerMois();
          });
        }
        if (state.pb.date) {
          choisiEl.hidden = false;
          choisiEl.innerHTML = 'Jour choisi : <strong>' + esc(pbDateLisible(state.pb.date)) + '</strong>';
        } else {
          choisiEl.hidden = true;
        }
      }
      dessinerMois();

      // Les fermetures arrivent après coup : on redessine pour griser les jours concernés.
      if (CAL && pbFermetures === null) {
        CAL.chargerFermetures(apiBase).then(function (f) {
          pbFermetures = f || [];
          if (body.querySelector('.mauto-devis-calendar-days')) dessinerMois();
        });
      }

      precBtn.addEventListener('click', function () {
        vueMois--; if (vueMois < 0) { vueMois = 11; vueAnnee--; }
        dessinerMois();
      });
      suivBtn.addEventListener('click', function () {
        vueMois++; if (vueMois > 11) { vueMois = 0; vueAnnee++; }
        dessinerMois();
      });

      var cr = body.querySelectorAll('[data-creneau]');
      var casePret = body.querySelector('#mauto-pb-pret');
      // Avec un véhicule de prêt, le dépôt se fait le matin du rendez-vous : c'est à ce
      // moment-là qu'on remet les clés du véhicule de remplacement.
      function appliquerReglePret() {
        var avecPret = casePret.checked;
        for (var k = 0; k < cr.length; k++) {
          var veille = cr[k].getAttribute('data-creneau').indexOf('veille') > -1;
          cr[k].disabled = avecPret && veille;
          cr[k].classList.toggle('is-off', avecPret && veille);
          if (avecPret && veille && cr[k].classList.contains('is-selected')) {
            cr[k].classList.remove('is-selected');
            state.pb.creneau = '';
          }
        }
        var note = body.querySelector('.mauto-pb-note-pret');
        if (note) note.hidden = !avecPret;
      }
      casePret.addEventListener('change', appliquerReglePret);
      appliquerReglePret();
      for (var i3 = 0; i3 < cr.length; i3++) {
        cr[i3].addEventListener('click', function (ev) {
          if (ev.currentTarget.disabled) return;
          state.pb.creneau = ev.currentTarget.getAttribute('data-creneau');
          for (var m = 0; m < cr.length; m++) cr[m].classList.remove('is-selected');
          ev.currentTarget.classList.add('is-selected');
          body.querySelector('.mauto-pb-error').hidden = true;
        });
      }

      body.querySelector('[data-action="pb-prev"]').addEventListener('click', renderPbCoordonnees);
      body.querySelector('[data-action="pb-next"]').addEventListener('click', function () {
        var e = body.querySelector('.mauto-pb-error');
        state.pb.pret = body.querySelector('#mauto-pb-pret').checked ? 'Oui' : 'Non';
        if (!state.pb.date) { e.textContent = 'Merci de choisir un jour dans le calendrier.'; e.hidden = false; return; }
        if (state.pb.date < minStr) { e.textContent = 'Le premier rendez-vous possible est le ' + pbDateLisible(minStr) + '.'; e.hidden = false; return; }
        if (!state.pb.creneau) { e.textContent = 'Merci de choisir votre mode de dépôt.'; e.hidden = false; return; }
        renderPbRecap();
      });
    }

    // ── 6/6 — Récapitulatif ────────────────────────────────────────────────────────────────
    function renderPbRecap() {
      setFlowActive(true);
      setStep(6, renderPbRdv);
      var droit;
      if (state.pb.brisGlace === 'Oui') {
        droit = 'Votre franchise vous est offerte, jusqu\'à 200 € et dans la limite de 20 % du montant TTC de la facture. Déclarez le bris de glace à votre assurance : nous lui transmettons le devis pour accord avant l\'intervention.';
      } else if (state.pb.brisGlace === 'Non') {
        droit = 'Sans garantie bris de glace, nous vous établissons un devis gratuit avant toute intervention.';
      } else {
        droit = 'Appelez votre assurance pour vérifier la garantie bris de glace et déclarer le sinistre : si elle est couverte, votre franchise vous est offerte jusqu\'à 200 €. Nous transmettons ensuite le devis à votre assureur.';
      }
      body.innerHTML =
        pbChrome(6, 'Récapitulatif', renderPbRdv) +
        '<p class="mauto-pb-question">Récapitulatif de votre demande</p>' +
        '<div class="mauto-pb-recap-grid">' +
          '<div><h4>Mon intervention</h4><p>' + esc(state.pb.vitrage) + (state.pb.dommage ? '<br>' + esc(state.pb.dommage) : '') + (state.pb.adas === 'Oui' ? '<br>Recalibrage caméra' : '') + '</p></div>' +
          '<div><h4>Mon véhicule</h4><p>' + esc(state.vehicule.marque + ' ' + state.vehicule.modele) + '<br>' + esc(state.vehicule.immatriculation) + '</p></div>' +
          '<div><h4>Mes informations</h4><p>' + esc(state.pb.prenom + ' ' + state.pb.nom) + '<br>' + esc(state.coordonnees.telephone) + '<br>' + esc(state.coordonnees.email) + '<br>Bris de glace : ' + esc(state.pb.brisGlace) + (state.pb.assureur ? '<br>' + esc(state.pb.assureur) : '') + '</p></div>' +
          '<div><h4>Mon rendez-vous</h4><p>' + esc(pbDateLisible(state.pb.date)) + '<br>' + esc(state.pb.creneau) + '<br>' + (state.pb.pret === 'Oui' ? 'Véhicule de prêt gratuit réservé' : 'Sans véhicule de prêt') + '</p></div>' +
        '</div>' +
        '<div class="mauto-pb-recap"><p>' + droit + '</p><p class="mauto-pb-recap-mini">Nous vous répondons sous 24 heures ouvrées pour confirmer le rendez-vous et commander votre vitrage. Offre valable jusqu\'au 31 décembre, non cumulable.</p></div>' +
        '<p class="mauto-devis-error mauto-pb-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="pb-prev">Je modifie</button>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="pb-send">Je confirme</button>' +
        '</div>';
      pbWire(renderPbRdv);
      body.querySelector('[data-action="pb-prev"]').addEventListener('click', renderPbRdv);
      body.querySelector('[data-action="pb-send"]').addEventListener('click', pbEnvoi);
    }

    function pbDescription() {
      var p = state.pb;
      var l = ['PRISE DE RENDEZ-VOUS VITRAGE — ' + (p.vitrage || 'vitrage à préciser')];
      if (p.dommage) l.push('Dommage : ' + p.dommage);
      if (p.adas) l.push('Caméra derrière le pare-brise : ' + p.adas);
      if (p.brisGlace) l.push('Garantie bris de glace : ' + p.brisGlace);
      if (p.assureur) l.push('Assurance : ' + p.assureur);
      if (p.date) l.push('Jour souhaité : ' + pbDateLisible(p.date));
      if (p.creneau) l.push(p.creneau);
      l.push(p.pret === 'Oui' ? 'VÉHICULE DE PRÊT demandé' : 'Sans véhicule de prêt');
      if (p.note) l.push('Note du client : ' + p.note);
      return l.join(' | ');
    }

    function pbEnvoi() {
      var btn = body.querySelector('[data-action="pb-send"]');
      var err = body.querySelector('.mauto-pb-error');
      err.hidden = true;
      btn.disabled = true;
      btn.textContent = 'Envoi…';
      state.autreDescription = pbDescription();
      state.prestationKey = AUTRE_KEY;
      var payload = {
        prestationKey: AUTRE_KEY,
        vehicule: {
          marque: state.vehicule.marque,
          modele: state.vehicule.modele,
          immatriculation: state.vehicule.immatriculation
        },
        pneu: state.pneu,
        nom: state.coordonnees.nom,
        telephone: state.coordonnees.telephone,
        email: state.coordonnees.email,
        message: state.autreDescription,
        source: presetKey ? 'page-prestation' : 'page-devis'
      };
      // Le parcours pare-brise est une vraie demande de rendez-vous : elle part sur /api/rdv,
      // qui déclenche le lien « Valider ce rendez-vous » de l'atelier, la création de l'OR et
      // l'email de confirmation au client (celui qui réclame l'attestation d'assurance).
      // Tant que le serveur déployé ne connaît pas encore cette prestation ni ces créneaux de
      // dépôt, il répond 400 : on retombe alors sur /api/devis, le comportement d'avant. Le jour
      // du déploiement, le parcours bascule tout seul, sans retoucher le thème.
      var payloadRdv = {
        prestations: ['Pare-brise (remplacement)'],
        autreDescription: state.autreDescription,
        dateSouhaitee: state.pb.date,
        depot: state.pb.creneau.indexOf('veille') > -1 ? 'veille' : 'matin',
        vehiculePret: state.pb.pret === 'Oui',
        client: {
          nom: state.pb.nom,
          prenom: state.pb.prenom,
          telephone: state.coordonnees.telephone,
          email: state.coordonnees.email,
          codePostal: ''
        },
        vehicule: payload.vehicule,
        photos: state.pb.photos
      };

      // Un envoi qui échoue ne doit JAMAIS faire échouer la chaîne : tant que le serveur déployé
      // ne connaît pas la route V2, il répond une page HTML 404 que r.json() refuse de lire. Sans
      // ce filet, le rejet sautait par-dessus le repli sur /api/devis et le client voyait une
      // erreur alors que sa demande pouvait très bien partir.
      var payloadRdvClassique = {
        prestation: 'Pare-brise (remplacement)',
        autreDescription: state.autreDescription,
        dateSouhaitee: state.pb.date,
        creneauSouhaite: state.pb.creneau,
        vehicule: payload.vehicule,
        nom: state.pb.nom,
        prenom: state.pb.prenom,
        telephone: state.coordonnees.telephone,
        email: state.coordonnees.email,
        codePostal: '',
        // Le véhicule de prêt et les photos voyagent aussi par cette porte : sans eux, l'atelier
        // ne voyait ni la demande de prêt sur la carte du planning, ni le vitrage avant le
        // rendez-vous. Un serveur qui ne connaît pas ces champs les ignore simplement.
        vehiculePret: state.pb.pret === 'Oui',
        photos: state.pb.photos,
        source: payload.source
      };

      function poster(url, corps) {
        return fetch(apiBase + url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(corps)
        }).then(function (r) {
          return r.json().then(
            function (j) { return { ok: r.ok && j && j.ok, body: j }; },
            function () { return { ok: false, body: null }; }
          );
        }, function () {
          return { ok: false, body: null };
        });
      }

      // Trois portes, de la plus complete a la plus ancienne : chaine RDV V2 (OR complete,
      // photos, email de confirmation), puis la route RDV classique (bouton de validation et OR),
      // puis la simple demande de devis. La premiere qui repond prend la demande.
      poster('/api/v2/public/rdv', payloadRdv)
        .then(function (res) {
          if (res.ok) return res;
          return poster('/api/rdv', payloadRdvClassique);
        })
        .then(function (res) {
          if (res.ok) return res;
          return poster('/api/devis', payload);
        })
        .then(function (res) {
          if (!res.ok) throw new Error((res.body && res.body.error) || 'Erreur serveur');
          renderPbConfirmation();
        })
        .catch(function () {
          err.textContent = 'Une erreur est survenue lors de l\'envoi. Merci de réessayer ou de nous appeler au 02 40 26 86 61.';
          err.hidden = false;
          btn.disabled = false;
          btn.textContent = 'Je confirme';
        });
    }

    function renderPbConfirmation() {
      setStep(6, null);
      // Envoi des photos : le formulaire ne peut pas héberger de fichiers (aucun stockage public
      // côté site), on ouvre donc l'email du client avec l'objet et le corps déjà écrits — il lui
      // reste à joindre ses trois photos depuis son téléphone.
      var sujet = encodeURIComponent('Photos pare-brise — ' + (state.vehicule.immatriculation || ''));
      var corps = encodeURIComponent(
        'Bonjour,\n\nVoici les photos pour ma demande de rendez-vous pare-brise.\n\n' +
        'Véhicule : ' + state.vehicule.marque + ' ' + state.vehicule.modele + ' (' + state.vehicule.immatriculation + ')\n' +
        'Nom : ' + state.pb.prenom + ' ' + state.pb.nom + '\n' +
        'Rendez-vous souhaité : ' + pbDateLisible(state.pb.date) + '\n\n' +
        'Pièces jointes à ajouter :\n- le vitrage en entier\n- la carte grise\n- l\'attestation d\'assurance, avec le numéro de contrat et l\'agence\n\nMerci.'
      );
      body.innerHTML =
        '<div class="mauto-pb-confirm">' +
          '<p class="mauto-pb-confirm-titre">Merci pour votre demande de rendez-vous !</p>' +
          '<p>Nous revenons vers vous <strong>sous 24 heures ouvrées</strong> pour confirmer le rendez-vous du <strong>' + esc(pbDateLisible(state.pb.date)) + '</strong> et commander votre vitrage.</p>' +
          (state.pb.pret === 'Oui' ? '<p>Votre <strong>véhicule de prêt est réservé</strong>, sans frais. Prévoyez votre permis et une carte bancaire pour l\'empreinte de caution.</p>' : '<p>Vous avez indiqué ne pas avoir besoin de véhicule de prêt. Dites-le nous si vous changez d\'avis.</p>') +
          '<div class="mauto-pb-photos">' +
            '<p class="mauto-pb-photos-titre">Une étape pour tenir la date : vos documents</p>' +
            '<p>Pour que votre assurance valide le devis <strong>avant le jour du rendez-vous</strong>, il nous faut les <strong>coordonnées complètes de votre assurance</strong> (compagnie, numéro de contrat, agence). Le plus simple : une photo de votre <strong>attestation d\'assurance</strong>, de votre <strong>carte grise</strong> et du <strong>vitrage en entier</strong>. Le message est déjà écrit, il ne reste qu\'à joindre les photos.</p>' +
            '<a class="mauto-devis-btn mauto-devis-btn-primary mauto-pb-photos-btn" href="mailto:m.autosudloire@gmail.com?subject=' + sujet + '&body=' + corps + '">Envoyer mes photos par email</a>' +
          '</div>' +
          '<p class="mauto-pb-confirm-tel">Une question ? <a href="tel:0240268661">02 40 26 86 61</a></p>' +
        '</div>';
    }

    // Cartes côte à côte pour les familles à choix forcé (ex. climatisation) — pas de
    // pré-sélection automatique, l'utilisateur doit choisir explicitement un forfait.
    function renderFamilyChoice(familyKey, showBack) {
      setFlowActive(true);
      setStep(1, showBack ? renderStep1 : null);
      var keys = FAMILIES[familyKey];
      var cardsHtml = keys.map(function (k) {
        var p = state.pricing[k];
        if (!p) return '';
        return (
          '<button type="button" class="mauto-devis-forfait-card" data-key="' + esc(k) + '">' +
            '<span class="mauto-devis-forfait-label">' + esc(p.label) + '</span>' +
            '<span class="mauto-devis-forfait-prix">' + esc(formatPrixAffiche(p)) + '</span>' +
          '</button>'
        );
      }).join('');
      body.innerHTML =
        '<p class="mauto-devis-field-label">Choisissez la formule qui vous convient :</p>' +
        '<div class="mauto-devis-forfaits">' + cardsHtml + '</div>' +
        (showBack
          ? '<div class="mauto-devis-actions"><button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button></div>'
          : '');
      var cards = body.querySelectorAll('.mauto-devis-forfait-card');
      for (var i = 0; i < cards.length; i++) {
        cards[i].addEventListener('click', function (ev) {
          var k = ev.currentTarget.getAttribute('data-key');
          state.prestationKey = k;
          goBackFromVehicule = function () { renderFamilyChoice(familyKey, showBack); };
          goToVehiculeOrEstimation();
        });
      }
      var back = body.querySelector('[data-action="back"]');
      if (back) back.addEventListener('click', renderStep1);
    }

    function renderQuickPicksBar() {
      if (!quickPicksBar) return;
      quickPicksBar.innerHTML = '';
      quickPicks.forEach(function (k) {
        // "k" peut être une clé tarifaire directe (vidange) ou le nom d'une famille
        // (freinage, climatisation) — dans ce cas on affiche un libellé générique.
        var isFamilyPick = !!FAMILIES[k];
        var viaAutre = !!QUICK_AUTRE[k];
        var displayKey = isFamilyPick ? FAMILIES[k][0] : k;
        var p = viaAutre ? null : state.pricing[displayKey];
        // Une clé sans tarif connu est ignorée : mieux vaut une vignette en moins qu'une
        // vignette qui mène à une erreur. Les vignettes de QUICK_AUTRE échappent à ce test,
        // elles n'ont volontairement pas de tarif.
        if (!p && !viaAutre) return;
        var label = QUICK_LABELS[k] || (isFamilyPick ? (FAMILY_LABELS[k] || k) : p.label);
        var btn = el(
          '<button type="button" class="mauto-devis-quickpick" data-key="' + esc(k) + '">' +
            '<span class="mauto-devis-quickpick-icon">' + iconFor(k) + '</span>' +
            '<span class="mauto-devis-quickpick-label">' + esc(label) + '</span>' +
          '</button>'
        );
        btn.addEventListener('click', function () {
          selectSuggestion(k);
          var all = quickPicksBar.querySelectorAll('.mauto-devis-quickpick');
          for (var i = 0; i < all.length; i++) all[i].classList.remove('is-active');
          btn.classList.add('is-active');
        });
        quickPicksBar.appendChild(btn);
      });
    }

    // Étape 2 — Véhicule : immatriculation/marque/modèle + chip de rappel de la prestation.
    // `p` est null quand cette étape est atteinte via validateCartOnly() pour un panier composé
    // uniquement de pneus (aucune prestation classique en cours) — dans ce cas pas de chip, et
    // "Continuer" saute directement aux Coordonnées plutôt qu'à l'Estimation (cf. plus bas).
    function renderStepVehicule() {
      setFlowActive(true);
      var p = state.prestationKey ? state.pricing[state.prestationKey] : null;

      // Sauvegarde ce qui est déjà tapé (même incomplet/invalide) avant de quitter l'étape en
      // arrière — la validation stricte reste appliquée au clic sur "Continuer". Définie avant
      // setStep() pour pouvoir servir à la fois à la flèche fixe mobile et au bouton inline.
      function captureVehiculeDraft() {
        state.vehicule = {
          immatriculation: body.querySelector('#mauto-devis-immatriculation').value,
          marque: body.querySelector('#mauto-devis-marque').value,
          modele: body.querySelector('#mauto-devis-modele').value,
        };
      }
      var backWithCapture = goBackFromVehicule ? function () { captureVehiculeDraft(); goBackFromVehicule(); } : null;
      setStep(2, backWithCapture);

      body.innerHTML =
        (p ?
          '<div class="mauto-devis-chip">' +
            '<span class="mauto-devis-chip-label">VOUS AVEZ SÉLECTIONNÉ&nbsp;: ' + esc(displayLabel(state.prestationKey, p)) + '</span>' +
            (goBackFromVehicule ? '<button type="button" class="mauto-devis-chip-remove" aria-label="Changer de prestation">✕</button>' : '') +
          '</div>'
        : '') +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-devis-immatriculation">Immatriculation *</label>' +
          '<input type="text" id="mauto-devis-immatriculation" name="immatriculation" placeholder="Ex. AB-123-CD" value="' + esc(state.vehicule.immatriculation) + '" required>' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-devis-marque">Marque *</label>' +
          '<input type="text" id="mauto-devis-marque" name="marque" placeholder="Ex. Peugeot" value="' + esc(state.vehicule.marque) + '" required>' +
        '</div>' +
        '<div class="mauto-devis-field">' +
          '<label for="mauto-devis-modele">Modèle *</label>' +
          '<input type="text" id="mauto-devis-modele" name="modele" placeholder="Ex. 208" value="' + esc(state.vehicule.modele) + '" required>' +
        '</div>' +
        '<p class="mauto-devis-error mauto-devis-vehicule-error" hidden></p>' +
        '<div class="mauto-devis-actions">' +
          (goBackFromVehicule ? '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' : '') +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="next">Continuer</button>' +
        '</div>';

      var chipRemove = body.querySelector('.mauto-devis-chip-remove');
      if (chipRemove) chipRemove.addEventListener('click', backWithCapture);
      var back = body.querySelector('[data-action="back"]');
      if (back) back.addEventListener('click', backWithCapture);

      body.querySelector('[data-action="next"]').addEventListener('click', function () {
        var immatRaw = body.querySelector('#mauto-devis-immatriculation').value.trim();
        var marque = body.querySelector('#mauto-devis-marque').value.trim();
        var modele = body.querySelector('#mauto-devis-modele').value.trim();
        var errorEl = body.querySelector('.mauto-devis-vehicule-error');

        if (!immatRaw || !marque || !modele) {
          errorEl.textContent = "Merci de renseigner l'immatriculation, la marque et le modèle.";
          errorEl.hidden = false;
          return;
        }
        if (!IMMAT_REGEX.test(immatRaw)) {
          errorEl.textContent = "Format d'immatriculation invalide — utilisez le format AB-123-CD.";
          errorEl.hidden = false;
          return;
        }
        errorEl.hidden = true;
        state.vehicule = { immatriculation: normalizeImmat(immatRaw), marque: marque, modele: modele };
        goToStep(state.prestationKey ? 3 : 4);
      });
    }

    // Étape 3 — Estimation : prix selon le mode + premier créneau envisageable.
    function renderStepEstimation() {
      setFlowActive(true);
      var p = state.pricing[state.prestationKey];
      setStep(3, backFromEstimation);

      // sur_devis : pas de texte intermédiaire ni de bouton "Continuer" — la popup s'affiche
      // immédiatement, ses propres boutons remplacent "← Retour"/"Continuer".
      if (p.mode === 'sur_devis') {
        body.innerHTML = '<div class="mauto-devis-estimation"><p class="mauto-devis-prestation-label">' + esc(displayLabel(state.prestationKey, p)) + '</p></div>';
        renderSurDevisModal();
        return;
      }

      var slot = formatDateLong(nextEligibleSlot());
      body.innerHTML =
        '<div class="mauto-devis-estimation">' +
          '<p class="mauto-devis-prestation-label">' + esc(displayLabel(state.prestationKey, p)) + '</p>' +
          '<p class="mauto-devis-prix">' + esc(formatPrixAffiche(p)) + '</p>' +
          '<p class="mauto-devis-creneau">Premier créneau envisageable : <strong>' + esc(slot) + '</strong> (sous réserve de confirmation par le garage)</p>' +
          '<div class="mauto-devis-estimation-choice">' +
            '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="rdv">Prendre rendez-vous</button>' +
            '<button type="button" class="mauto-devis-btn mauto-devis-btn-secondary" data-action="devis">Demander un devis</button>' +
          '</div>' +
          '<p class="mauto-devis-estimation-choice-hint">« Prendre rendez-vous » réserve directement un créneau (toutes les prestations de votre panier, le cas échéant). « Demander un devis » vous permet d\'obtenir un chiffrage écrit avant de vous engager sur une date.</p>' +
          '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost mauto-devis-btn-add" data-action="ajouter">' + icone('plus') + ' Ajouter une autre prestation</button>' +
          '<div class="mauto-devis-actions">' +
            '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' +
          '</div>' +
        '</div>';
      body.querySelector('[data-action="back"]').addEventListener('click', backFromEstimation);
      body.querySelector('[data-action="devis"]').addEventListener('click', function () { goToStep(4); });
      body.querySelector('[data-action="rdv"]').addEventListener('click', handoffToRdv);
      body.querySelector('[data-action="ajouter"]').addEventListener('click', addCurrentToCart);
    }

    // "Prendre rendez-vous" depuis l'étape Estimation (prix connu uniquement, cf. ci-dessus) :
    // passe la main entière au widget RDV dédié (devis-rdv-widget.js) plutôt que de recréer son
    // parcours ici — prestation(s) et véhicule sont déjà connus à ce stade (l'étape 2 Véhicule a
    // forcément été validée avant d'atteindre l'étape 3 pour la 1ère prestation du panier), il ne
    // reste que date/créneau/coordonnées à recueillir, ce que le widget RDV fait déjà tel quel.
    // `detail.prestations` est toujours un tableau désormais (longueur 1 = comportement identique
    // à l'ancien `detail.prestation` singulier ; ≥2 = panier, cf. devis-rdv-widget.js qui gère les
    // deux cas). Chaque libellé correspond à une entrée de RDV_PRESTATIONS (devis-rdv-widget.js) —
    // libellés copiés-collés depuis la même grille tarifaire serveur, cf. commentaire de ce
    // fichier. L'événement DOM (plutôt qu'un appel direct) garde ce widget agnostique de
    // l'existence d'un overlay RDV séparé — c'est devis-floating-widget.js qui sait ouvrir/fermer
    // les overlays et écoute cet événement.
    function handoffToRdv() {
      var items = state.cart.concat([{ prestationKey: state.prestationKey }]);
      var labels = items.map(cartItemLabel);
      document.dispatchEvent(new CustomEvent('mauto:open-rdv-widget', {
        detail: {
          prestations: labels,
          vehicule: {
            immatriculation: state.vehicule.immatriculation,
            marque: state.vehicule.marque,
            modele: state.vehicule.modele,
          },
        },
      }));
    }

    // Popup "non chiffrable en ligne" — s'affiche au clic sur "Continuer" à l'étape Estimation
    // quand la prestation est en mode sur_devis, avant d'avancer vers les coordonnées.
    function renderSurDevisModal() {
      var modal = el(
        '<div class="mauto-devis-modal-backdrop">' +
          '<div class="mauto-devis-modal">' +
            '<p class="mauto-devis-modal-title">Oups&nbsp;! Votre demande n\'est pas chiffrable en ligne</p>' +
            '<p class="mauto-devis-modal-text">Nous ne pouvons pas encore calculer votre devis en ligne pour cette prestation, mais nous y travaillons&nbsp;!</p>' +
            '<p class="mauto-devis-modal-help">Besoin d\'aide&nbsp;? <a href="tel:0240268661">02 40 26 86 61</a></p>' +
            '<div class="mauto-devis-modal-actions">' +
              '<button type="button" class="mauto-devis-btn mauto-devis-btn-primary" data-action="transmit">Transmettre ma demande au réparateur</button>' +
              '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost mauto-devis-btn-add" data-action="ajouter">' + icone('plus') + ' Ajouter une autre prestation</button>' +
              '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="modify">Modifier ma recherche</button>' +
            '</div>' +
          '</div>' +
        '</div>'
      );
      container.appendChild(modal);
      modal.querySelector('[data-action="transmit"]').addEventListener('click', function () {
        modal.remove();
        goToStep(4);
      });
      modal.querySelector('[data-action="ajouter"]').addEventListener('click', function () {
        modal.remove();
        addCurrentToCart();
      });
      modal.querySelector('[data-action="modify"]').addEventListener('click', function () {
        modal.remove();
        renderStep1();
      });
    }

    // Montant numérique utilisé dans le total estimatif du panier : le plus bas connu (prix
    // fixe, ou borne basse d'une fourchette/"à partir de") — jamais un sur_devis. Logique ad.fr
    // (cf. leurs mentions légales sur le "montant total approximatif et estimatif"). Dupliqué
    // côté serveur (sync-server/server.js, /api/devis-groupe) qui recalcule et fait foi à l'email.
    function itemPrixNumerique(p) {
      if (!p) return null;
      if (p.mode === 'fixe' && typeof p.prix_fixe === 'number') return p.prix_fixe;
      if (p.mode === 'fourchette' && typeof p.min === 'number') return p.min;
      if (p.mode === 'a_partir_de' && typeof p.a_partir_de === 'number') return p.a_partir_de;
      return null;
    }

    // Panier complet = items déjà "ajoutés" + celui en cours de finalisation (sur lequel le
    // client vient de cliquer "Demander un devis"/"Transmettre ma demande"). Pas d'item courant
    // à ajouter quand state.prestationKey est vide (panier composé uniquement de pneus validé via
    // validateCartOnly()).
    function allCartItemsWithCurrent() {
      var items = state.cart.slice();
      if (state.prestationKey) {
        items.push({
          prestationKey: state.prestationKey,
          pneu: state.pneu,
          message: messagePrestation(),
        });
      }
      return items;
    }

    // Récap multi-lignes + total estimatif affichés à l'étape Coordonnées quand le panier
    // contient au moins 2 prestations (item courant inclus). Les prestations sur-devis sont
    // listées mais explicitement exclues du total, avec la mention demandée.
    function cartRecapAndTotalHtml() {
      var items = allCartItemsWithCurrent();
      var rows = '';
      var total = 0;
      var hasChiffre = false;
      var hasSurDevis = false;
      items.forEach(function (item) {
        if (item.type === 'pneu_produit') {
          var montantPneu = pneuProduitMontant(item);
          total += montantPneu;
          hasChiffre = true;
          rows += '<li>' + esc(cartItemLabel(item)) + ' × ' + esc(item.quantite) + ' — ' + esc(formatEuro(montantPneu)) + '</li>';
          return;
        }
        var p = item.prestationKey === AUTRE_KEY ? AUTRE_PRESTATION : state.pricing[item.prestationKey];
        if (!p) return;
        if (p.mode === 'sur_devis') {
          hasSurDevis = true;
          rows += '<li>' + esc(p.label) + ' — prix communiqué après diagnostic, non inclus dans le total ci-dessous</li>';
        } else {
          var montant = itemPrixNumerique(p);
          if (montant != null) { total += montant; hasChiffre = true; }
          rows += '<li>' + esc(p.label) + ' — ' + esc(formatPrixAffiche(p)) + '</li>';
        }
      });
      var totalHtml = hasChiffre
        ? '<p class="mauto-devis-cart-total">Total estimatif TTC : <strong>' + esc(formatEuro(total)) + '</strong></p>' +
          '<p class="mauto-devis-cart-total-note">Cette estimation additionne les montants connus ci-dessus' + (hasSurDevis ? ' (hors prestations sur devis, dont le prix sera communiqué après diagnostic)' : '') + ' — ce n\'est pas un engagement ferme.</p>'
        : '';
      return '<ul class="mauto-devis-cart-recap-list">' + rows + '</ul>' + totalHtml;
    }

    // Étape 4 — Coordonnées : nom/téléphone/email + bouton final. N'est jamais atteinte que via
    // le choix explicite "Demander un devis" (prix connu) ou "Transmettre ma demande au
    // réparateur" (sur_devis) — jamais via "Prendre rendez-vous", qui bifurque entièrement vers
    // le widget RDV avant cette étape (cf. handoffToRdv). Le libellé est donc toujours "devis".
    function renderStepCoordonnees() {
      setFlowActive(true);
      var p = state.prestationKey ? state.pricing[state.prestationKey] : null;
      var isGroupe = state.cart.length > 0;
      var submitLabel = isGroupe ? 'Demander mon devis groupé' : 'Demander un devis';
      var recapHtml = isGroupe
        ? cartRecapAndTotalHtml()
        : '<p class="mauto-devis-recap">' + esc(displayLabel(state.prestationKey, p)) + ' — ' + esc(formatPrixAffiche(p)) + '</p>';

      // Sauvegarde ce qui est déjà tapé (même incomplet) avant de revenir à l'estimation — la
      // validation stricte reste appliquée à la soumission finale.
      function saveCoordDraft() {
        state.coordonnees = {
          nom: body.querySelector('#mauto-devis-nom').value,
          telephone: body.querySelector('#mauto-devis-telephone').value,
          email: body.querySelector('#mauto-devis-email').value,
        };
      }
      // Panier composé uniquement de pneus (validé via validateCartOnly()) : pas d'Estimation à
      // revoir (jamais atteinte pour ce cas), retour au choix de prestation à la place.
      var backHandler = state.prestationKey
        ? function () { saveCoordDraft(); goToStep(3); }
        : function () { saveCoordDraft(); goToStep(1); };
      setStep(4, backHandler);

      body.innerHTML =
        '<form class="mauto-devis-form">' +
          recapHtml +
          '<div class="mauto-devis-field">' +
            '<label for="mauto-devis-nom">Nom *</label>' +
            '<input type="text" id="mauto-devis-nom" name="nom" value="' + esc(state.coordonnees.nom) + '" required>' +
          '</div>' +
          '<div class="mauto-devis-field">' +
            '<label for="mauto-devis-telephone">Téléphone *</label>' +
            '<input type="tel" id="mauto-devis-telephone" name="telephone" value="' + esc(state.coordonnees.telephone) + '" required>' +
          '</div>' +
          '<div class="mauto-devis-field">' +
            '<label for="mauto-devis-email">Email *</label>' +
            '<input type="email" id="mauto-devis-email" name="email" value="' + esc(state.coordonnees.email) + '" required>' +
          '</div>' +
          '<p class="mauto-devis-error mauto-devis-form-error" hidden></p>' +
          '<div class="mauto-devis-actions">' +
            '<button type="button" class="mauto-devis-btn mauto-devis-btn-ghost" data-action="back">← Retour</button>' +
            '<button type="submit" class="mauto-devis-btn mauto-devis-btn-primary">' + esc(submitLabel) + '</button>' +
          '</div>' +
        '</form>';
      body.querySelector('[data-action="back"]').addEventListener('click', backHandler);
      body.querySelector('form').addEventListener('submit', onSubmit);
    }

    function renderDone() {
      body.innerHTML =
        '<div class="mauto-devis-done">' +
          '<p class="mauto-devis-done-title">' + icone('valide') + ' Votre demande de devis a bien été envoyée</p>' +
          '<p>Un email de confirmation vient de vous être adressé. Nous revenons vers vous sous 24h ouvrées avec un devis chiffré.</p>' +
        '</div>';
    }

    function onSubmit(ev) {
      ev.preventDefault();
      var form = ev.target;
      var errorEl = form.querySelector('.mauto-devis-form-error');
      errorEl.hidden = true;

      var nom = form.nom.value.trim();
      var telephone = form.telephone.value.trim();
      var email = form.email.value.trim();

      if (!nom || !telephone || !email) {
        errorEl.textContent = 'Merci de renseigner votre nom, téléphone et email.';
        errorEl.hidden = false;
        return;
      }
      state.coordonnees = { nom: nom, telephone: telephone, email: email };

      var submitBtn = form.querySelector('button[type="submit"]');
      var originalLabel = submitBtn.textContent;
      submitBtn.disabled = true;
      submitBtn.textContent = 'Envoi…';

      // Panier vide = comportement mono-prestation strictement inchangé (même payload, même
      // endpoint). Panier non vide = devis groupé : la prestation en cours de finalisation
      // rejoint les items déjà ajoutés dans un seul tableau `prestations`.
      var isGroupe = state.cart.length > 0;
      var payload, endpoint;
      if (isGroupe) {
        payload = {
          prestations: allCartItemsWithCurrent().map(function (item) {
            if (item.type === 'pneu_produit') {
              return {
                type: 'pneu_produit',
                productId: item.productId,
                productHandle: item.productHandle,
                marque: item.marque,
                modele: item.modele,
                dimension: item.dimension,
                saison: item.saison,
                quantite: item.quantite,
                prixPneuUnitaire: item.prixPneuUnitaire,
                prixMontageUnitaire: item.prixMontageUnitaire,
              };
            }
            return { prestationKey: item.prestationKey, pneu: item.pneu, message: item.message };
          }),
          vehicule: {
            marque: state.vehicule.marque,
            modele: state.vehicule.modele,
            immatriculation: state.vehicule.immatriculation,
          },
          nom: nom,
          telephone: telephone,
          email: email,
          source: presetKey ? 'page-prestation' : 'page-devis',
        };
        endpoint = '/api/devis-groupe';
      } else {
        payload = {
          prestationKey: state.prestationKey,
          vehicule: {
            marque: state.vehicule.marque,
            modele: state.vehicule.modele,
            immatriculation: state.vehicule.immatriculation,
          },
          pneu: state.pneu,
          nom: nom,
          telephone: telephone,
          email: email,
          message: messagePrestation(),
          source: presetKey ? 'page-prestation' : 'page-devis',
        };
        endpoint = '/api/devis';
      }

      fetch(apiBase + endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok || !res.body.ok) throw new Error((res.body && res.body.error) || 'Erreur serveur');
          renderDone();
        })
        .catch(function (err) {
          errorEl.textContent = "Une erreur est survenue lors de l'envoi. Merci de réessayer ou de nous appeler directement.";
          errorEl.hidden = false;
          submitBtn.disabled = false;
          submitBtn.textContent = originalLabel;
        });
    }

    renderLoading();
    fetch(apiBase + '/api/pricing')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data.ok) throw new Error(data.error || 'Erreur tarifs');
        state.pricing = data.pricing;
        // Ajoutée uniquement ici (jamais dans FAMILIES ni dans les clés fournies par le
        // serveur) : n'apparaît donc que dans les listes dérivées de Object.keys(state.pricing)
        // — barre de recherche et menu déroulant complet — jamais dans les dropdowns de famille
        // scopée ni les icônes de raccourci.
        state.pricing[AUTRE_KEY] = AUTRE_PRESTATION;
        state.pricing[PB_KEY] = PB_PRESTATION;
        renderQuickPicksBar();
        var commande = readCommandeParams();
        if (commande) {
          // Le client vient de l'état vide des résultats : sa dimension est déjà connue, on saute
          // les 6 étapes et on l'emmène directement à la suite du devis.
          state.prestationKey = DEVIS_PNEU_KEY;
          state.pneu = commande;
          state.pneuCommande = true;
          goBackFromVehicule = null;
          startPneuFlow(goToVehiculeOrEstimation, true);
        } else if (presetKey === PB_KEY) {
          goBackFromVehicule = null;
          startPareBriseFlow(null);
        } else if (isSingle) {
          goBackFromVehicule = null;
          state.prestationKey = presetKey;
          if (presetKey === DEVIS_PNEU_KEY) startPneuFlow(function () { goToStep(2); });
          else goToStep(2);
        } else if (isFamily && FORCE_CHOICE[presetKey]) {
          renderFamilyChoice(presetKey, false);
        } else if (isFamily) {
          goBackFromVehicule = renderStep1;
          state.prestationKey = FAMILIES[presetKey][0];
          goToStep(2);
        } else {
          renderStep1();
        }
      })
      .catch(function () {
        renderError('Impossible de charger les tarifs pour le moment. Merci de réessayer plus tard ou de nous appeler directement.');
      });
  }

  // Badge Google (bloc hero) : reflète en direct la note affichée par le widget d'avis Google
  // déjà installé sur le site (app Reputon — badge flottant présent sur toutes les pages, cf.
  // éléments .reputon-rating-count / .reputon-reviews-count). Reputon ne fournit pas d'API
  // publique, donc pas de nouvelle intégration ici : on lit simplement le texte déjà rendu par
  // Reputon sur la même page, avec quelques tentatives car Reputon charge sa donnée de façon
  // asynchrone après le rendu initial. Si Reputon est absent ou n'a pas encore répondu après
  // ~8s, le texte codé en dur du réglage de section (repli) reste affiché tel quel.
  function syncGoogleBadge() {
    var badgeTextEls = document.querySelectorAll('.mauto-devis-hero__badgetext');
    if (!badgeTextEls.length) return;

    function applyFromReputon() {
      var rateEl = document.querySelector('.reputon-rating-count');
      var countEl = document.querySelector('.reputon-reviews-count');
      var rating = rateEl && rateEl.textContent.trim();
      var countMatch = countEl && /\d+/.exec(countEl.textContent);
      if (!rating || !countMatch) return false;
      var text = rating.replace('.', ',') + '/5 sur ' + countMatch[0] + ' avis Google';
      for (var i = 0; i < badgeTextEls.length; i++) badgeTextEls[i].textContent = text;
      return true;
    }

    if (applyFromReputon()) return;
    var attempts = 0;
    var interval = setInterval(function () {
      attempts++;
      if (applyFromReputon() || attempts >= 16) clearInterval(interval);
    }, 500);
  }

  function init() {
    // [data-mauto-devis-lazy] : conteneur monté à la demande (ex. overlay du bouton flottant),
    // jamais initialisé automatiquement au chargement — cf. devis-floating-widget.js qui appelle
    // window.MautoDevisWidget.initWidget(container) lui-même à l'ouverture.
    var containers = document.querySelectorAll('[data-mauto-devis-widget]:not([data-mauto-devis-lazy])');
    for (var i = 0; i < containers.length; i++) initWidget(containers[i]);
    syncGoogleBadge();
  }

  // Exposée pour devis-floating-widget.js (montage à la demande du widget dans l'overlay).
  window.MautoDevisWidget = { initWidget: initWidget };

  // Boutons « Prendre rendez-vous » / « Demander un devis » des pages prestation : ils amènent
  // directement dans le widget de la page, en douceur, sans rechargement. Posé ici plutôt que
  // dans un <script> de gabarit : ce fichier est chargé sur toutes les pages qui portent un
  // widget, donc le comportement suit le widget où qu'il soit.
  document.addEventListener('click', function (ev) {
    var lien = ev.target.closest ? ev.target.closest('a[href^="#devis-"]') : null;
    if (!lien) return;
    var cible = null;
    var tous = document.querySelectorAll('[data-mauto-devis-widget]');
    for (var i = 0; i < tous.length; i++) {
      if (!tous[i].closest('.mauto-devis-overlay')) { cible = tous[i]; break; }
    }
    if (!cible) return;
    ev.preventDefault();
    var haut = cible.getBoundingClientRect().top + window.pageYOffset - 70;
    if (haut < 0) haut = 0;
    try {
      window.scrollTo({ top: haut, behavior: 'smooth' });
    } catch (e) {
      window.scrollTo(0, haut);
    }
    // Filet : certains navigateurs (et les aperçus intégrés) ignorent le défilement animé.
    // Si rien n'a bougé, on y va directement plutôt que de laisser le visiteur sur place.
    setTimeout(function () {
      if (Math.abs(window.pageYOffset - haut) > 80) window.scrollTo(0, haut);
    }, 450);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
